import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, LogOut, Users, BadgeCheck, Briefcase, MessageSquare,
  Clock, CheckCircle, XCircle, ExternalLink, Instagram, Youtube,
  Trash2, Eye, EyeOff, TrendingUp, LayoutDashboard, MapPin, User,
  Link as LinkIcon, Star, IndianRupee, Handshake, FileText, Wallet, Building2,
  Tag, Plus, Camera, Film, Music, Mic, PenTool, Smartphone, Zap,
  Clapperboard, Gamepad2, Menu, X, Search, AlertTriangle, ChevronRight, Sparkles,
  Send, Bell, Crown, Image, Download, Trophy, Share2, Copy, UserPlus, Phone, MessageCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/App";
import html2canvas from "html2canvas";

// ==================== Helpers ====================
const getAdminHeaders = () => {
  const token = localStorage.getItem("adminToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getYouTubeThumbnail = (url) => {
  if (!url) return "";
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m && m[1]) return `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg`;
  }
  return "";
};

const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtINR = (n) => `₹${fmt(n)}`;

// ==================== Shared UI atoms ====================
const GlassCard = ({ children, className = "" }) => (
  <div className={`relative rounded-2xl bg-white/[0.03] backdrop-blur-xl border border-white/10 ${className}`}>
    {children}
  </div>
);

const SectionHeader = ({ title, subtitle, icon: Icon, right }) => (
  <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
    <div className="flex items-start gap-3">
      {Icon && (
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF0080] to-[#B026FF] flex items-center justify-center shadow-[0_0_25px_rgba(255,0,128,0.35)]">
          <Icon className="w-5 h-5 text-white" />
        </div>
      )}
      <div>
        <h1 className="font-manrope font-extrabold text-2xl md:text-3xl tracking-tight text-white">{title}</h1>
        {subtitle && <p className="text-sm text-white/50 mt-1">{subtitle}</p>}
      </div>
    </div>
    {right}
  </div>
);

const StatTile = ({ label, value, icon: Icon, accent = "from-[#FF0080] to-[#B026FF]", sub }) => (
  <GlassCard className="p-5 overflow-hidden group hover:border-white/20 transition">
    <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br ${accent} opacity-20 blur-2xl group-hover:opacity-40 transition`} />
    <div className="relative flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</p>
        <p className="font-manrope font-extrabold text-3xl mt-1.5 text-white">{value}</p>
        {sub && <p className="text-xs text-white/40 mt-1">{sub}</p>}
      </div>
      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center shadow-lg shrink-0`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </GlassCard>
);

const StatusBadge = ({ status }) => {
  const map = {
    pending: "bg-amber-500/20 text-amber-300 border-amber-400/30",
    approved: "bg-emerald-500/20 text-emerald-300 border-emerald-400/30",
    rejected: "bg-rose-500/20 text-rose-300 border-rose-400/30",
    paid: "bg-emerald-500/20 text-emerald-300 border-emerald-400/30",
    completed: "bg-emerald-500/20 text-emerald-300 border-emerald-400/30",
    disputed: "bg-orange-500/20 text-orange-300 border-orange-400/30",
    in_progress: "bg-blue-500/20 text-blue-300 border-blue-400/30",
    refunded: "bg-slate-500/20 text-slate-300 border-slate-400/30",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider border ${map[status] || "bg-white/5 text-white/60 border-white/10"}`}>
      {status}
    </span>
  );
};

// ==================== Main Component ====================
const AdminDashboard = () => {
  const navigate = useNavigate();

  // ---------- State (preserved from old dashboard) ----------
  const [stats, setStats] = useState(null);
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [influencers, setInfluencers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [pendingBrands, setPendingBrands] = useState([]);
  const [talents, setTalents] = useState([]);
  const [talentFilter, setTalentFilter] = useState("all");
  // ---------- Signups (all registered users, including those without a profile) ----------
  const [signups, setSignups] = useState([]);
  const [signupsStats, setSignupsStats] = useState(null);
  const [signupFilter, setSignupFilter] = useState("all"); // all | star | brand | incomplete | completed
  const [signupSearch, setSignupSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewProfileRequest, setViewProfileRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const [adminUser, setAdminUser] = useState(null);

  const [starSettings, setStarSettings] = useState({ selected_ids: [], selected_stars: [] });
  const [selectedStarIds, setSelectedStarIds] = useState([]);

  const [newPromoForm, setNewPromoForm] = useState({
    code: "", advertiser_name: "", advertiser_contact: "", discount_percent: 50, commission_per_use: 50
  });
  const [creatingPromo, setCreatingPromo] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationResults, setMigrationResults] = useState([]);
  const [deleteNames, setDeleteNames] = useState("");
  const [deletingProfiles, setDeletingProfiles] = useState(false);

  const [opportunities, setOpportunities] = useState([]);
  const [opportunityApplications, setOpportunityApplications] = useState([]);
  const [showCreateOpportunity, setShowCreateOpportunity] = useState(false);
  const [newOpportunity, setNewOpportunity] = useState({
    title: "", description: "", category: "influencers", payment_amount: 49, payment_type: "per_post",
    platform: "Instagram", requirements: "", deadline_days: 7, max_applications: 100,
    min_followers: 100, verified_only: true, is_active: true
  });

  const [landingContent, setLandingContent] = useState({
    featured_influencers: [], featured_brands: [], testimonial_videos: [], influencer_testimonial_videos: []
  });
  const [newVideo, setNewVideo] = useState({ language: "English", video_url: "", thumbnail_url: "", quote: "", name: "", company: "" });
  const [newInfluencerVideo, setNewInfluencerVideo] = useState({ language: "English", video_url: "", thumbnail_url: "", quote: "", name: "", followers: "" });
  const [savingLanding, setSavingLanding] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  // Collab Card Generator
  const [showCollabCardModal, setShowCollabCardModal] = useState(false);
  const [selectedBrandForCard, setSelectedBrandForCard] = useState(null);
  const [selectedStarForCard, setSelectedStarForCard] = useState(null);
  const [generatingCard, setGeneratingCard] = useState(false);

  // Quick Onboard State
  const [quickOnboardForm, setQuickOnboardForm] = useState({
    name: "",
    phone: "",
    instagram_handle: "",
    category: "influencers",
    city: ""
  });
  const [quickOnboardLoading, setQuickOnboardLoading] = useState(false);
  const [quickOnboardResult, setQuickOnboardResult] = useState(null);
  const [quickSignupDrafts, setQuickSignupDrafts] = useState([]);

  // Insta Content Generator
  const [instaContentType, setInstaContentType] = useState("new-pro-creator");
  const [selectedCreatorForInsta, setSelectedCreatorForInsta] = useState(null);
  const [demoCreatorName, setDemoCreatorName] = useState("");
  const [demoCreatorCategory, setDemoCreatorCategory] = useState("Fashion & Lifestyle");
  const [demoCreatorPhoto, setDemoCreatorPhoto] = useState(null);
  const [demoPortfolioImages, setDemoPortfolioImages] = useState([]);
  const [generatingInstaCard, setGeneratingInstaCard] = useState(false);

  // ---------- New state: sidebar, deals ----------
  const [activeSection, setActiveSection] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [deals, setDeals] = useState([]);
  const [overdueDeals, setOverdueDeals] = useState([]);
  
  // Brand Pro Syncs
  const [brandProSyncs, setBrandProSyncs] = useState([]);

  // ---------- Push Notifications state ----------
  const [pushForm, setPushForm] = useState({
    audience: "all",
    user_id: "",
    title: "",
    body: "",
    deep_link: "",
    custom_deep_link: "",
  });
  const [pushSending, setPushSending] = useState(false);
  const [pushReach, setPushReach] = useState(null);
  const [pushHistory, setPushHistory] = useState([]);
  const [pushUserSearch, setPushUserSearch] = useState("");
  const [pushUserResults, setPushUserResults] = useState([]);
  const [pushUserPicked, setPushUserPicked] = useState(null);

  // Fetch reach + history when entering notifications section
  useEffect(() => {
    if (activeSection !== "notifications") return;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/push/tokens`, {
          headers: getAdminHeaders(),
          params: { audience: pushForm.audience === "user" ? "all" : pushForm.audience, limit: 1, skip: 0 },
        });
        if (pushForm.audience !== "user") setPushReach(r.data?.total ?? 0);
      } catch (e) {
        setPushReach(null);
      }
      try {
        const r = await axios.get(`${API}/admin/push/broadcasts`, { headers: getAdminHeaders(), params: { limit: 20 } });
        setPushHistory(r.data?.items || []);
      } catch (e) {
        setPushHistory([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  // ---------- Effects ----------
  useEffect(() => {
    const storedAdmin = localStorage.getItem("adminUser");
    if (!storedAdmin) { navigate("/admin"); return; }
    try {
      const admin = JSON.parse(storedAdmin);
      if (admin.user_type !== "admin") { navigate("/admin"); return; }
      setAdminUser(admin);
      fetchData();
      fetchOpportunities();
      fetchDeals();
      fetchBrandProSyncs();
    } catch (e) { navigate("/admin"); }
    // eslint-disable-next-line
  }, [navigate]);

  // ---------- Data fetchers ----------
  const fetchData = async () => {
    try {
      const headers = getAdminHeaders();
      const [statsRes, requestsRes, influencersRes, brandsRes, starsRes, withdrawalsRes, promoRes, pendingBrandsRes, landingRes, talentsRes, signupsRes, signupsStatsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/verification-requests`, { headers }),
        axios.get(`${API}/admin/influencers`, { headers }),
        axios.get(`${API}/admin/brands`, { headers }),
        axios.get(`${API}/admin/stars-of-the-day`, { headers }),
        axios.get(`${API}/admin/withdrawals`, { headers }),
        axios.get(`${API}/admin/promo-codes`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/pending-brands`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/landing-content`, { headers }).catch(() => ({ data: { featured_influencers: [], featured_brands: [], testimonial_videos: [] } })),
        axios.get(`${API}/admin/talents`, { headers }).catch(() => ({ data: { talents: [] } })),
        axios.get(`${API}/admin/users`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/signups-stats`, { headers }).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setVerificationRequests(requestsRes.data);
      setInfluencers(influencersRes.data);
      setBrands(brandsRes.data);
      setStarSettings(starsRes.data);
      setSelectedStarIds(starsRes.data.selected_ids || []);
      setWithdrawals(withdrawalsRes.data);
      setPromoCodes(promoRes.data);
      setPendingBrands(pendingBrandsRes.data);
      setLandingContent(landingRes.data || { featured_influencers: [], featured_brands: [], testimonial_videos: [] });
      setTalents(talentsRes.data?.talents || []);
      setSignups(Array.isArray(signupsRes.data) ? signupsRes.data : []);
      setSignupsStats(signupsStatsRes.data || null);
    } catch (error) {
      console.error("Admin data fetch error:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchOpportunities = async () => {
    try {
      const [oppsRes, appsRes] = await Promise.all([
        axios.get(`${API}/admin/opportunities`, { headers: getAdminHeaders() }),
        axios.get(`${API}/admin/opportunity-applications`, { headers: getAdminHeaders() })
      ]);
      setOpportunities(oppsRes.data || []);
      setOpportunityApplications(appsRes.data || []);
    } catch (error) { console.error("Failed to fetch opportunities:", error); }
  };

  const fetchDeals = async () => {
    try {
      const [dealsRes, overdueRes] = await Promise.all([
        axios.get(`${API}/admin/deals`, { headers: getAdminHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/overdue-deals`, { headers: getAdminHeaders() }).catch(() => ({ data: [] })),
      ]);
      setDeals(dealsRes.data || []);
      setOverdueDeals(overdueRes.data || []);
    } catch (error) { console.error("Failed to fetch deals:", error); }
  };

  const fetchBrandProSyncs = async () => {
    try {
      const res = await axios.get(`${API}/admin/brand-pro-syncs`, { headers: getAdminHeaders() });
      setBrandProSyncs(res.data?.syncs || []);
    } catch (error) { console.error("Failed to fetch brand pro syncs:", error); }
  };

  // ---------- Handlers (all preserved) ----------
  const createOpportunity = async () => {
    try {
      await axios.post(`${API}/admin/opportunities`, newOpportunity, { headers: getAdminHeaders() });
      toast.success("Opportunity created!");
      setShowCreateOpportunity(false);
      setNewOpportunity({
        title: "", description: "", category: "influencers", payment_amount: 49, payment_type: "per_post",
        platform: "Instagram", requirements: "", deadline_days: 7, max_applications: 100, min_followers: 100, verified_only: true, is_active: true
      });
      fetchOpportunities();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to create opportunity"); }
  };

  const toggleOpportunityActive = async (oppId, currentActive) => {
    try {
      await axios.put(`${API}/admin/opportunities/${oppId}`, { is_active: !currentActive }, { headers: getAdminHeaders() });
      toast.success(currentActive ? "Opportunity paused" : "Opportunity activated");
      fetchOpportunities();
    } catch (error) { toast.error("Failed to update opportunity"); }
  };

  const handleApplicationAction = async (applicationId, action) => {
    try {
      await axios.post(`${API}/admin/opportunity-applications/${applicationId}/${action}`, {}, { headers: getAdminHeaders() });
      toast.success(action === "approve" ? "Application approved!" : action === "reject" ? "Application rejected" : "Work verified & payment released!");
      fetchOpportunities();
    } catch (error) { toast.error(error.response?.data?.detail || "Action failed"); }
  };

  const handleProcessWithdrawal = async (withdrawalId, action) => {
    const confirmMsg = action === "approve"
      ? "Mark this withdrawal as paid? Make sure you have transferred the money!"
      : "Reject this withdrawal request?";
    if (!confirm(confirmMsg)) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/withdrawals/${withdrawalId}/process?action=${action}`, {}, { headers: getAdminHeaders() });
      toast.success(action === "approve" ? "Withdrawal marked as paid!" : "Withdrawal rejected");
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to process withdrawal"); }
    finally { setProcessing(false); }
  };

  const handleSetStars = async () => {
    if (selectedStarIds.length === 0) { toast.error("Please select at least one influencer"); return; }
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/stars-of-the-day`, { influencer_ids: selectedStarIds }, { headers: getAdminHeaders() });
      toast.success("Stars of the Day updated!");
      fetchData();
    } catch (error) { toast.error("Failed to update stars"); }
    finally { setProcessing(false); }
  };

  const handleClearStars = async () => {
    setProcessing(true);
    try {
      await axios.delete(`${API}/admin/stars-of-the-day`, { headers: getAdminHeaders() });
      toast.success("Stars cleared - using automatic selection");
      setSelectedStarIds([]);
      fetchData();
    } catch (error) { toast.error("Failed to clear stars"); }
    finally { setProcessing(false); }
  };

  const toggleStarSelection = (userId) => {
    setSelectedStarIds(prev => {
      if (prev.includes(userId)) return prev.filter(id => id !== userId);
      if (prev.length < 5) return [...prev, userId];
      toast.error("Maximum 5 stars can be selected");
      return prev;
    });
  };

  const runMigration = async () => {
    setMigrationRunning(true);
    setMigrationResults([]);
    const migrations = [
      { name: "Migrate All Influencers to Talents", endpoint: "/migrate-all-influencers-to-talents" },
      { name: "Add Live Verified Profiles", endpoint: "/add-live-verified-profiles" },
      { name: "Migrate Paid Verified Users", endpoint: "/migrate-paid-verified-users" },
      { name: "Sync Talents to Users (Fix Login)", endpoint: "/sync-talents-to-users" }
    ];
    const results = [];
    for (const m of migrations) {
      try {
        const response = await axios.post(`${API}${m.endpoint}`, {}, { headers: getAdminHeaders() });
        results.push({ name: m.name, success: true, message: response.data.message || "Success", data: response.data });
      } catch (error) {
        results.push({ name: m.name, success: false, message: error.response?.data?.detail || "Failed" });
      }
      setMigrationResults([...results]);
    }
    setMigrationRunning(false);
    toast.success("Migration complete!");
    fetchData();
  };

  const toggleUserVisibility = async (userId) => {
    try {
      const response = await axios.put(`${API}/admin/users/${userId}/toggle-visibility`, {}, { headers: getAdminHeaders() });
      toast.success(response.data.message);
      fetchData();
    } catch (error) { toast.error("Failed to toggle visibility"); }
  };

  const handleDeleteProfilesByName = async () => {
    if (!deleteNames.trim()) { toast.error("Please enter profile names"); return; }
    const names = deleteNames.split(",").map(n => n.trim()).filter(n => n);
    if (names.length === 0) { toast.error("Please enter valid names"); return; }
    setDeletingProfiles(true);
    try {
      const response = await axios.post(`${API}/admin/delete-profiles`, { names }, { headers: getAdminHeaders() });
      toast.success(response.data.message);
      setDeleteNames("");
      setShowDeleteModal(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to delete profiles"); }
    finally { setDeletingProfiles(false); }
  };

  const handleApproveBrand = async (userId) => {
    try {
      await axios.put(`${API}/admin/brands/${userId}/approve`, {}, { headers: getAdminHeaders() });
      toast.success("Brand approved! Email sent.");
      fetchData();
    } catch (error) { toast.error("Failed to approve brand"); }
  };

  const handleRejectBrand = async (userId) => {
    const reason = prompt("Enter rejection reason (optional):");
    try {
      await axios.put(`${API}/admin/brands/${userId}/reject`, { reason }, { headers: getAdminHeaders() });
      toast.success("Brand rejected");
      fetchData();
    } catch (error) { toast.error("Failed to reject brand"); }
  };

  const handleToggleProBrand = async (userId, currentProStatus) => {
    try {
      const response = await axios.put(`${API}/admin/brands/${userId}/toggle-pro`, {}, { headers: getAdminHeaders() });
      toast.success(response.data.message);
      fetchData();
    } catch (error) { 
      toast.error(error.response?.data?.detail || "Failed to toggle Pro Brand status"); 
    }
  };

  const toggleFeaturedInfluencer = (userId) => {
    setLandingContent(prev => {
      const current = prev.featured_influencers || [];
      if (current.includes(userId)) return { ...prev, featured_influencers: current.filter(id => id !== userId) };
      if (current.length < 6) return { ...prev, featured_influencers: [...current, userId] };
      toast.error("Maximum 6 influencers allowed");
      return prev;
    });
  };

  const toggleFeaturedBrand = (userId) => {
    setLandingContent(prev => {
      const current = prev.featured_brands || [];
      if (current.includes(userId)) return { ...prev, featured_brands: current.filter(id => id !== userId) };
      if (current.length < 6) return { ...prev, featured_brands: [...current, userId] };
      toast.error("Maximum 6 brands allowed");
      return prev;
    });
  };

  const addTestimonialVideo = async () => {
    if (!newVideo.video_url || !newVideo.name || !newVideo.company) { toast.error("Please fill video URL, name, and company"); return; }
    const updated = { ...landingContent, testimonial_videos: [...(landingContent.testimonial_videos || []), { ...newVideo, id: Date.now() }] };
    setLandingContent(updated);
    setNewVideo({ language: "English", video_url: "", thumbnail_url: "", quote: "", name: "", company: "" });
    try { await axios.post(`${API}/admin/landing-content`, updated, { headers: getAdminHeaders() }); toast.success("Video added & saved!"); }
    catch { toast.error("Added locally. Click 'Save Changes'."); }
  };

  const removeTestimonialVideo = async (id) => {
    const updated = { ...landingContent, testimonial_videos: (landingContent.testimonial_videos || []).filter(v => v.id !== id) };
    setLandingContent(updated);
    try { await axios.post(`${API}/admin/landing-content`, updated, { headers: getAdminHeaders() }); toast.success("Removed"); }
    catch { toast.error("Failed to save"); }
  };

  const saveLandingContent = async () => {
    setSavingLanding(true);
    try {
      await axios.post(`${API}/admin/landing-content`, landingContent, { headers: getAdminHeaders() });
      toast.success("Landing page saved!");
      fetchData();
    } catch (error) { toast.error("Failed to save landing content"); }
    finally { setSavingLanding(false); }
  };

  const generateVideoThumbnail = (videoFile) => new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "auto"; video.muted = true; video.playsInline = true; video.crossOrigin = "anonymous";
    let resolved = false;
    const cleanup = () => { if (video.src) URL.revokeObjectURL(video.src); };
    const safeResolve = (v) => { if (!resolved) { resolved = true; cleanup(); resolve(v); } };
    const timeout = setTimeout(() => safeResolve(null), 10000);
    const captureFrame = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 360;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => { clearTimeout(timeout); safeResolve(blob); }, "image/jpeg", 0.8);
      } catch { clearTimeout(timeout); safeResolve(null); }
    };
    video.onloadedmetadata = () => {
      const seekTime = Math.min(1, video.duration * 0.1);
      if (seekTime > 0 && video.duration > 0) video.currentTime = seekTime;
    };
    video.onseeked = captureFrame;
    video.onloadeddata = () => { if (!resolved && video.readyState >= 2) setTimeout(() => { if (!resolved) captureFrame(); }, 500); };
    video.onerror = () => { clearTimeout(timeout); safeResolve(null); };
    video.src = URL.createObjectURL(videoFile);
    video.load();
  });

  const handleVideoUpload = async (file) => {
    if (!file) return null;
    setUploadingVideo(true);
    try {
      toast.info("Uploading video...");
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/admin/upload-testimonial-video`, formData, {
        headers: { ...getAdminHeaders(), "Content-Type": "multipart/form-data" }, timeout: 120000
      });
      const videoUrl = res.data.url;
      toast.success("Video uploaded!");
      const thumbnailBlob = await generateVideoThumbnail(file);
      if (thumbnailBlob) {
        const thumbFormData = new FormData();
        thumbFormData.append("file", thumbnailBlob, "thumbnail.jpg");
        try {
          const thumbRes = await axios.post(`${API}/admin/upload-testimonial-video`, thumbFormData, {
            headers: { ...getAdminHeaders(), "Content-Type": "multipart/form-data" }
          });
          return { videoUrl, thumbnailUrl: thumbRes.data.url };
        } catch { return { videoUrl, thumbnailUrl: "" }; }
      }
      return { videoUrl, thumbnailUrl: "" };
    } catch (error) { toast.error(error.response?.data?.detail || "Video upload failed"); return null; }
    finally { setUploadingVideo(false); }
  };

  const addInfluencerTestimonialVideo = async () => {
    if (!newInfluencerVideo.video_url || !newInfluencerVideo.name) { toast.error("Please fill video URL and name"); return; }
    const updated = { ...landingContent, influencer_testimonial_videos: [...(landingContent.influencer_testimonial_videos || []), { ...newInfluencerVideo, id: Date.now() }] };
    setLandingContent(updated);
    setNewInfluencerVideo({ language: "English", video_url: "", thumbnail_url: "", quote: "", name: "", followers: "" });
    try { await axios.post(`${API}/admin/landing-content`, updated, { headers: getAdminHeaders() }); toast.success("Video added & saved!"); }
    catch { toast.error("Added locally. Click 'Save Changes'."); }
  };

  const removeInfluencerTestimonialVideo = async (id) => {
    const updated = { ...landingContent, influencer_testimonial_videos: (landingContent.influencer_testimonial_videos || []).filter(v => v.id !== id) };
    setLandingContent(updated);
    try { await axios.post(`${API}/admin/landing-content`, updated, { headers: getAdminHeaders() }); toast.success("Removed"); }
    catch { toast.error("Failed to save"); }
  };

  const handleApprove = async (requestId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/verification-requests/${requestId}`, { action: "approve" }, { headers: getAdminHeaders() });
      toast.success("Verification approved!");
      fetchData();
      setSelectedRequest(null);
    } catch { toast.error("Failed to approve"); }
    finally { setProcessing(false); }
  };

  const handleReject = async (requestId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/verification-requests/${requestId}`, { action: "reject", reason: rejectReason }, { headers: getAdminHeaders() });
      toast.success("Verification rejected");
      fetchData();
      setSelectedRequest(null);
      setRejectReason("");
    } catch { toast.error("Failed to reject"); }
    finally { setProcessing(false); }
  };

  const handleToggleVerification = async (userId) => {
    try {
      await axios.put(`${API}/admin/influencers/${userId}/verify`, {}, { headers: getAdminHeaders() });
      toast.success("Status updated");
      fetchData();
    } catch { toast.error("Failed to update"); }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm("Delete this user?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: getAdminHeaders() });
      toast.success("User deleted");
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { headers: getAdminHeaders() }); } catch {}
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    navigate("/admin");
  };

  const handleCreatePromoCode = async (e) => {
    e.preventDefault();
    if (!newPromoForm.code || !newPromoForm.advertiser_name || !newPromoForm.advertiser_contact) { toast.error("Please fill all required fields"); return; }
    setCreatingPromo(true);
    try {
      await axios.post(`${API}/admin/promo-codes`, newPromoForm, { headers: getAdminHeaders() });
      toast.success(`Promo code ${newPromoForm.code.toUpperCase()} created!`);
      setNewPromoForm({ code: "", advertiser_name: "", advertiser_contact: "", discount_percent: 50, commission_per_use: 50 });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to create promo code"); }
    finally { setCreatingPromo(false); }
  };

  const handleDeletePromoCode = async (promoId) => {
    if (!confirm("Delete this promo code?")) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${promoId}`, { headers: getAdminHeaders() });
      toast.success("Deleted");
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  const handleMarkPromoPaid = async (promoId, amount) => {
    const payAmount = prompt("Enter amount being paid:", amount);
    if (!payAmount) return;
    try {
      await axios.post(`${API}/admin/promo-codes/${promoId}/mark-paid?amount=${parseInt(payAmount)}`, {}, { headers: getAdminHeaders() });
      toast.success(`Marked ₹${payAmount} as paid`);
      fetchData();
    } catch { toast.error("Failed to update payout"); }
  };

  // NEW: Resolve disputed deal
  const handleResolveDispute = async (dealId, action) => {
    const confirmMsg = action === "release_to_influencer"
      ? "Release payment to influencer?"
      : "Refund to brand? Process actual refund in Razorpay dashboard.";
    if (!confirm(confirmMsg)) return;
    try {
      await axios.post(`${API}/admin/deals/${dealId}/resolve?action=${action}`, {}, { headers: getAdminHeaders() });
      toast.success("Dispute resolved");
      fetchDeals();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to resolve"); }
  };

  const handleIssueWarning = async (dealId) => {
    if (!confirm("Issue overdue warning to influencer?")) return;
    try {
      await axios.post(`${API}/admin/deals/${dealId}/issue-warning`, {}, { headers: getAdminHeaders() });
      toast.success("Warning issued");
      fetchDeals();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
  };

  // ---------- Derived ----------
  const pendingRequests = useMemo(() => verificationRequests.filter(r => r.status === "pending"), [verificationRequests]);
  const pendingWithdrawals = useMemo(() => withdrawals.filter(w => w.status === "pending"), [withdrawals]);
  const pendingApplications = useMemo(() => opportunityApplications.filter(a => a.status === "pending"), [opportunityApplications]);
  const submittedApplications = useMemo(() => opportunityApplications.filter(a => a.status === "submitted"), [opportunityApplications]);
  const disputedDeals = useMemo(() => deals.filter(d => d.status === "disputed"), [deals]);

  // ==================== Section renderers ====================

  // --- Overview (NEW) ---
  const renderOverview = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Overview"
        subtitle="Real-time pulse of the FoundStar platform"
        icon={LayoutDashboard}
        right={
          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-white/60">Live</span>
            </div>
            <Button onClick={() => setShowMigrationModal(true)} className="bg-white/5 hover:bg-white/10 border border-white/10 text-white">
              <Sparkles className="w-4 h-4 mr-2 text-[#FF4DA6]" /> Run Migration
            </Button>
          </div>
        }
      />

      {/* KPIs grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatTile label="Total Stars" value={fmt(stats?.total_talents)} icon={Users} accent="from-[#FF0080] to-[#B026FF]" />
        <StatTile label="Pro Stars" value={fmt(stats?.verified_talents)} icon={BadgeCheck} accent="from-[#B026FF] to-[#7B2FBE]" />
        <StatTile label="Total Brands" value={fmt(stats?.total_brands)} icon={Briefcase} accent="from-[#FF0080] to-[#FF4DA6]" />
        <StatTile label="Pending Verifs" value={fmt(stats?.pending_verifications)} icon={Clock} accent="from-amber-500 to-orange-500" />
        <StatTile label="Collaborations" value={fmt(stats?.total_collaborations)} icon={Handshake} accent="from-emerald-500 to-teal-500" />
        <StatTile label="Conversations" value={fmt(stats?.total_conversations)} icon={MessageSquare} accent="from-sky-500 to-indigo-500" />
        <StatTile label="Influencers" value={fmt(stats?.talent_categories?.influencers)} icon={Smartphone} accent="from-pink-500 to-rose-500" />
        <StatTile label="Models" value={fmt(stats?.talent_categories?.models)} icon={Camera} accent="from-rose-500 to-pink-500" />
      </div>

      {/* Action queue */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <GlassCard className="p-5 cursor-pointer hover:border-[#FF0080]/40 transition" onClick={() => setActiveSection("signups")}>
          <div className="flex items-center justify-between mb-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">New Signups</p>
            <ChevronRight className="w-4 h-4 text-white/30" />
          </div>
          <p className="font-manrope font-extrabold text-4xl text-white">{fmt(signupsStats?.total_signups ?? signups.length)}</p>
          <p className="text-sm text-white/60 mt-1">
            {signupsStats?.signups_last_24h ? `+${signupsStats.signups_last_24h} in last 24h` : `${signupsStats?.incomplete_signups ?? signups.filter(u => !u.profile_completed).length} pending profile`}
          </p>
        </GlassCard>
        <GlassCard className="p-5 cursor-pointer hover:border-[#FF0080]/40 transition" onClick={() => setActiveSection("verifications")}>
          <div className="flex items-center justify-between mb-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">Action Queue</p>
            <ChevronRight className="w-4 h-4 text-white/30" />
          </div>
          <p className="font-manrope font-extrabold text-4xl text-white">{pendingRequests.length}</p>
          <p className="text-sm text-white/60 mt-1">Pending verifications</p>
        </GlassCard>
        <GlassCard className="p-5 cursor-pointer hover:border-orange-400/40 transition" onClick={() => setActiveSection("pending-brands")}>
          <div className="flex items-center justify-between mb-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">Brand Approvals</p>
            <ChevronRight className="w-4 h-4 text-white/30" />
          </div>
          <p className="font-manrope font-extrabold text-4xl text-white">{pendingBrands.length}</p>
          <p className="text-sm text-white/60 mt-1">Awaiting review</p>
        </GlassCard>
        <GlassCard className="p-5 cursor-pointer hover:border-emerald-400/40 transition" onClick={() => setActiveSection("withdrawals")}>
          <div className="flex items-center justify-between mb-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">Withdrawals</p>
            <ChevronRight className="w-4 h-4 text-white/30" />
          </div>
          <p className="font-manrope font-extrabold text-4xl text-white">{pendingWithdrawals.length}</p>
          <p className="text-sm text-white/60 mt-1">Payouts pending</p>
        </GlassCard>
      </div>

      {/* Overdue deals alert */}
      {overdueDeals.length > 0 && (
        <GlassCard className="p-5 border-orange-400/30 bg-orange-500/5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
            </div>
            <div className="flex-1">
              <p className="font-manrope font-bold text-white">{overdueDeals.length} overdue deals need attention</p>
              <p className="text-sm text-white/60">Some active deals have crossed their deadline.</p>
            </div>
            <Button onClick={() => setActiveSection("deals")} className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 border border-orange-400/30">
              Review
            </Button>
          </div>
        </GlassCard>
      )}
    </div>
  );

  // --- Verifications ---
  const renderVerifications = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Verification Requests"
        subtitle={`${pendingRequests.length} pending · ${verificationRequests.length} total`}
        icon={BadgeCheck}
      />
      {verificationRequests.length === 0 ? (
        <GlassCard className="p-16 text-center">
          <BadgeCheck className="w-12 h-12 text-white/20 mx-auto mb-3" />
          <p className="text-white/50">No verification requests</p>
        </GlassCard>
      ) : (
        <div className="space-y-3">
          {verificationRequests.map((req) => (
            <GlassCard key={req.request_id} className="p-5 hover:border-white/20 transition">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <Avatar className="w-14 h-14 border-2 border-white/10">
                    <AvatarImage src={req.picture?.startsWith("http") ? req.picture : req.picture ? `${API}/files/${req.picture}` : null} />
                    <AvatarFallback className="bg-gradient-to-br from-[#FF0080] to-[#B026FF] text-white font-bold">
                      {req.name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-manrope font-bold text-white">{req.name}</span>
                      <StatusBadge status={req.status} />
                      {req.payment_id ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
                          Paid ₹199
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase bg-amber-500/20 text-amber-300 border border-amber-400/30">
                          Unpaid
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-white/50 mb-2">{req.email}</p>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-white/60 mb-2">
                      <span className="flex items-center gap-1 font-mono"><Users className="w-3.5 h-3.5 text-[#FF4DA6]" />{fmt(req.followers_count)}</span>
                      {req.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#B026FF]" />{req.location}</span>}
                      {req.platform && <span className="text-white/40">· {req.platform}</span>}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-1">
                      {req.niches?.slice(0, 4).map((n, i) => (
                        <span key={i} className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/60">{n}</span>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {req.instagram_link && (
                        <a href={req.instagram_link.startsWith("http") ? req.instagram_link : `https://${req.instagram_link}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md bg-pink-500/20 text-pink-300 hover:bg-pink-500/30">
                          <Instagram className="w-3 h-3" />IG<ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      )}
                      {req.youtube_link && (
                        <a href={req.youtube_link.startsWith("http") ? req.youtube_link : `https://${req.youtube_link}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md bg-red-500/20 text-red-300 hover:bg-red-500/30">
                          <Youtube className="w-3 h-3" />YT<ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      )}
                      {req.platform_link && (
                        <a href={req.platform_link.startsWith("http") ? req.platform_link : `https://${req.platform_link}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md bg-white/5 text-white/60 hover:bg-white/10">
                          <LinkIcon className="w-3 h-3" />Link<ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setViewProfileRequest(req)} className="bg-white/5 hover:bg-white/10 text-white" data-testid={`view-profile-${req.request_id}`}>
                    <Eye className="w-4 h-4 mr-1" />View
                  </Button>
                  {req.status === "pending" && (
                    <>
                      <Button size="sm" onClick={() => setSelectedRequest(req)} className="bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-400/30" data-testid={`reject-${req.request_id}`}>
                        <XCircle className="w-4 h-4 mr-1" />Reject
                      </Button>
                      <Button size="sm" onClick={() => handleApprove(req.request_id)} disabled={processing} className="bg-gradient-to-r from-[#FF0080] to-[#B026FF] hover:opacity-90 text-white" data-testid={`approve-${req.request_id}`}>
                        <CheckCircle className="w-4 h-4 mr-1" />Approve
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );

  // --- Signups (all registered users — including those without a profile yet) ---
  const renderSignups = () => {
    const fmtDate = (iso) => {
      if (!iso) return "—";
      try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return "—";
        return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
      } catch { return "—"; }
    };

    // Apply filter + search
    const term = signupSearch.trim().toLowerCase();
    const filtered = signups.filter((u) => {
      // Filter
      if (signupFilter === "star" && !["influencer", "talent"].includes(u.user_type)) return false;
      if (signupFilter === "brand" && u.user_type !== "brand") return false;
      if (signupFilter === "completed" && !u.profile_completed) return false;
      if (signupFilter === "incomplete" && u.profile_completed) return false;
      // Search
      if (!term) return true;
      const blob = `${u.name || ""} ${u.email || ""} ${u.user_type || ""}`.toLowerCase();
      return blob.includes(term);
    });

    const stats = signupsStats || {
      total_signups: signups.length,
      star_signups: signups.filter(u => ["influencer", "talent"].includes(u.user_type)).length,
      brand_signups: signups.filter(u => u.user_type === "brand").length,
      completed_star_profiles: signups.filter(u => u.has_talent_profile).length,
      completed_brand_profiles: signups.filter(u => u.has_brand_profile).length,
      incomplete_signups: signups.filter(u => !u.profile_completed).length,
      signups_last_24h: 0,
    };

    const filterChips = [
      { id: "all", label: "All", count: signups.length },
      { id: "star", label: "Stars", count: stats.star_signups },
      { id: "brand", label: "Brands", count: stats.brand_signups },
      { id: "completed", label: "Profile Completed", count: signups.filter(u => u.profile_completed).length },
      { id: "incomplete", label: "Profile Pending", count: stats.incomplete_signups },
    ];

    const handleDeleteSignup = async (uid, name) => {
      if (!window.confirm(`Delete signup "${name}"? This will remove the user account.`)) return;
      try {
        await axios.delete(`${API}/admin/users/${uid}`, { headers: getAdminHeaders() });
        toast.success("User deleted");
        fetchData();
      } catch {
        toast.error("Failed to delete");
      }
    };

    const handleRemindOne = async (uid, name) => {
      try {
        const r = await axios.post(`${API}/admin/signups/${uid}/remind`, {}, { headers: getAdminHeaders() });
        if (r.data?.success) {
          toast.success(r.data.email_sent ? `Reminder emailed to ${name}` : (r.data.message || "Reminder logged"));
        } else {
          toast.info(r.data?.message || "No reminder sent");
        }
        fetchData();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to send reminder");
      }
    };

    const handleRemindAllPending = async () => {
      const pendingCount = signups.filter(u => !u.profile_completed).length;
      if (pendingCount === 0) {
        toast.info("No pending signups to remind");
        return;
      }
      if (!window.confirm(`Send a 'complete your profile' email to ${pendingCount} pending signup(s)?`)) return;
      try {
        const r = await axios.post(`${API}/admin/signups/remind-all-pending`, {}, { headers: getAdminHeaders() });
        toast.success(r.data?.message || "Reminders sent");
        fetchData();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to send reminders");
      }
    };

    const fmtRelative = (iso) => {
      if (!iso) return null;
      try {
        const t = new Date(iso).getTime();
        if (isNaN(t)) return null;
        const diff = Date.now() - t;
        const m = Math.floor(diff / 60000);
        if (m < 1) return "just now";
        if (m < 60) return `${m}m ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `${h}h ago`;
        const d = Math.floor(h / 24);
        return `${d}d ago`;
      } catch { return null; }
    };

    return (
      <div className="space-y-6">
        <SectionHeader
          title="Signups"
          subtitle="Every registered user — visible the instant they sign up"
          icon={Users}
          right={
            <div className="flex items-center gap-2">
              <Button
                onClick={handleRemindAllPending}
                className="bg-gradient-to-r from-[#FF0080] to-[#B026FF] hover:opacity-90 text-white"
              >
                <Send className="w-4 h-4 mr-2" /> Remind All Pending
              </Button>
              <Button
                variant="ghost"
                className="text-white/70 hover:text-white border border-white/10"
                onClick={() => fetchData()}
              >
                <Sparkles className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>
          }
        />

        {/* Stat tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatTile
            label="Total Signups"
            value={fmt(stats.total_signups)}
            icon={Users}
            sub={stats.signups_last_24h ? `+${stats.signups_last_24h} in last 24h` : "All-time"}
          />
          <StatTile
            label="Star Signups"
            value={fmt(stats.star_signups)}
            icon={Star}
            accent="from-[#FF4D6D] to-[#FF0080]"
          />
          <StatTile
            label="Brand Signups"
            value={fmt(stats.brand_signups)}
            icon={Briefcase}
            accent="from-[#B026FF] to-[#7C3AED]"
          />
          <StatTile
            label="Profile Pending"
            value={fmt(stats.incomplete_signups)}
            icon={Clock}
            accent="from-[#FF6B00] to-[#FF4D6D]"
            sub="Signed up, no profile yet"
          />
        </div>

        {/* Filter chips + search */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-2">
            {filterChips.map((c) => {
              const active = signupFilter === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setSignupFilter(c.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-manrope font-medium transition ${
                    active
                      ? "text-white shadow-[0_0_20px_rgba(255,0,128,0.4)] bg-gradient-to-r from-[#FF0080] to-[#B026FF]"
                      : "bg-white/5 text-white/60 hover:bg-white/10 border border-white/10"
                  }`}
                >
                  {c.label}
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono ${active ? "bg-white/20" : "bg-white/10"}`}>
                    {fmt(c.count)}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="relative ml-auto w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              placeholder="Search name, email…"
              value={signupSearch}
              onChange={(e) => setSignupSearch(e.target.value)}
              className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#FF0080]"
            />
          </div>
        </div>

        {/* Table */}
        <GlassCard className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5 bg-white/[0.02]">
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">User</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Type</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Profile Status</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Signed Up</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const isBrand = u.user_type === "brand";
                  const completed = u.profile_completed;
                  return (
                    <tr key={u.user_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-9 h-9">
                            <AvatarImage src={u.picture} />
                            <AvatarFallback className="bg-gradient-to-br from-[#FF0080] to-[#B026FF] text-white text-xs">
                              {(u.name || u.email || "?").charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="font-manrope font-semibold text-sm text-white flex items-center gap-1 truncate">
                              {u.name || "—"}
                              {u.is_verified && <BadgeCheck className="w-3.5 h-3.5 text-[#B026FF]" />}
                            </p>
                            <p className="text-xs text-white/40 truncate">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-3">
                        <span
                          className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full text-white"
                          style={{ background: isBrand ? "#B026FF" : "#FF0080" }}
                        >
                          {isBrand ? "Brand" : "Star"}
                        </span>
                      </td>
                      <td className="p-3">
                        {completed ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                            <CheckCircle className="w-3.5 h-3.5" /> Completed
                            {!isBrand && u.talent_category && (
                              <span className="ml-1 text-[10px] text-emerald-200/80">· {u.talent_category}</span>
                            )}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-orange-500/15 text-orange-300 border border-orange-500/30">
                            <Clock className="w-3.5 h-3.5" /> Pending
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-xs text-white/60 font-mono whitespace-nowrap">
                        {fmtDate(u.created_at)}
                        {u.last_reminded_at && (
                          <p className="text-[10px] text-orange-300/70 mt-0.5">
                            Reminded {fmtRelative(u.last_reminded_at)} · {u.reminder_count}x
                          </p>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          {!completed && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-[#FF4DA6] hover:bg-[#FF0080]/10"
                              title={u.last_reminded_at ? `Send another reminder (sent ${fmtRelative(u.last_reminded_at)})` : "Send profile reminder email"}
                              onClick={() => handleRemindOne(u.user_id, u.name || u.email)}
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-rose-400 hover:bg-rose-500/10"
                            title="Delete user"
                            onClick={() => handleDeleteSignup(u.user_id, u.name || u.email)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="py-12 text-center text-white/40">
                <Users className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p>No signups match this filter</p>
              </div>
            )}
          </div>
        </GlassCard>
      </div>
    );
  };


  // --- Talents (creators) ---
  const renderTalents = () => {
    const categories = [
      { id: "all", name: "All", icon: Users, color: "#FF0080" },
      { id: "influencers", name: "Influencers", icon: Smartphone, color: "#E91E63" },
      { id: "models", name: "Models", icon: Camera, color: "#FF4D6D" },
      { id: "actors", name: "Actors", icon: Film, color: "#B026FF" },
      { id: "dancers", name: "Dancers", icon: Music, color: "#FF6B00" },
      { id: "photographers", name: "Photo", icon: Camera, color: "#00A86B" },
      { id: "editors", name: "Editors", icon: Clapperboard, color: "#14B8A6" },
      { id: "gaming", name: "Gaming", icon: Gamepad2, color: "#8B5CF6" },
      { id: "content_writers", name: "Writers", icon: PenTool, color: "#FFD700" },
      { id: "voice_artists", name: "Voice", icon: Mic, color: "#1A73E8" },
    ];
    const categoryColors = { models: "#FF4D6D", actors: "#B026FF", dancers: "#FF6B00", voice_artists: "#1A73E8", photographers: "#00A86B", content_writers: "#FFD700", influencers: "#E91E63", editors: "#14B8A6", gaming: "#8B5CF6" };
    const filtered = talents.filter(t => talentFilter === "all" || t.category === talentFilter);
    return (
      <div className="space-y-6">
        <SectionHeader title="Creators" subtitle={`${talents.length} profiles across 9 categories`} icon={Users} />
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => {
            const Icon = cat.icon;
            const count = cat.id === "all" ? talents.length : talents.filter(t => t.category === cat.id).length;
            const active = talentFilter === cat.id;
            return (
              <button key={cat.id} onClick={() => setTalentFilter(cat.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-manrope font-medium transition ${active ? "text-white shadow-[0_0_20px_rgba(255,0,128,0.4)]" : "bg-white/5 text-white/60 hover:bg-white/10 border border-white/10"}`}
                style={active ? { background: `linear-gradient(135deg, ${cat.color}, ${cat.color}AA)` } : {}}>
                <Icon className="w-3.5 h-3.5" />{cat.name}
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono ${active ? "bg-white/20" : "bg-white/10"}`}>{count}</span>
              </button>
            );
          })}
        </div>
        <GlassCard className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5 bg-white/[0.02]">
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Star</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Category</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">City</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Rate</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Followers</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Status</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.user_id} className={`border-b border-white/5 hover:bg-white/[0.02] ${t.is_hidden ? "opacity-60" : ""}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-9 h-9"><AvatarImage src={t.photos?.[0] || t.picture} /><AvatarFallback className="bg-gradient-to-br from-[#FF0080] to-[#B026FF] text-white text-xs">{t.name?.charAt(0)}</AvatarFallback></Avatar>
                        <div>
                          <p className="font-manrope font-semibold text-sm text-white flex items-center gap-1">{t.name}{t.is_verified && <BadgeCheck className="w-3.5 h-3.5 text-[#B026FF]" />}{t.is_hidden && <EyeOff className="w-3 h-3 text-rose-400" title="Hidden from brands" />}</p>
                          <p className="text-xs text-white/40">{t.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3"><span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full text-white" style={{ background: categoryColors[t.category] || "#FF0080" }}>{t.category}</span></td>
                    <td className="p-3 text-sm text-white/60">{t.city || "—"}</td>
                    <td className="p-3 text-sm font-mono text-[#FF4DA6]">₹{fmt(t.hourly_rate)}/hr</td>
                    <td className="p-3 text-sm font-mono text-white">{fmt(t.followers_count)}</td>
                    <td className="p-3"><StatusBadge status={t.is_verified ? "approved" : "pending"} /></td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-400 hover:bg-blue-500/10" title="Share profile link"
                          onClick={async () => {
                            try {
                              const res = await axios.get(`${API}/stars/${t.user_id}/share-link`, { headers: getAdminHeaders() });
                              await navigator.clipboard.writeText(res.data.profile_url);
                              toast.success(`Profile link copied!\n${res.data.profile_url}`, { duration: 3000 });
                            } catch (e) {
                              toast.error("Failed to get share link");
                            }
                          }}>
                          <Share2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className={`h-8 w-8 ${t.is_verified ? "text-emerald-400" : "text-white/40"} hover:bg-white/10`} title="Toggle verification"
                          onClick={async () => { try { await axios.put(`${API}/admin/talents/${t.user_id}/verify`, {}, { headers: getAdminHeaders() }); toast.success("Updated"); fetchData(); } catch { toast.error("Failed"); } }}>
                          <BadgeCheck className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className={`h-8 w-8 ${t.is_hidden ? "text-emerald-400" : "text-white/60"} hover:bg-white/10`} title={t.is_hidden ? "Unhide creator" : "Hide creator from brands"}
                          onClick={() => toggleUserVisibility(t.user_id)}>
                          {t.is_hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-400 hover:bg-rose-500/10" title="Permanently delete creator"
                          onClick={async () => {
                            if (!window.confirm(`Permanently delete ${t.name}?\n\nThis will remove their account, profile, listings, ratings, portfolio and bookings. This cannot be undone.`)) return;
                            try {
                              await axios.delete(`${API}/admin/talents/${t.user_id}`, { headers: getAdminHeaders() });
                              toast.success(`${t.name} deleted permanently`);
                              fetchData();
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Failed to delete");
                            }
                          }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && <div className="py-12 text-center text-white/40"><Users className="w-10 h-10 mx-auto mb-2 opacity-40" /><p>No talents found</p></div>}
          </div>
        </GlassCard>
      </div>
    );
  };

  // --- Brands ---
  const renderBrands = () => (
    <div className="space-y-6">
      <SectionHeader title="Brands" subtitle={`${brands.length} approved brands`} icon={Briefcase} />
      <GlassCard className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5 bg-white/[0.02]">
                <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Brand</th>
                <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Niche</th>
                <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Pro</th>
                <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Requests</th>
                <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Deals</th>
                <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Actions</th>
              </tr>
            </thead>
            <tbody>
              {brands.map((b) => (
                <tr key={b.user_id} className={`border-b border-white/5 hover:bg-white/[0.02] ${b.is_hidden ? "opacity-60" : ""}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-9 h-9"><AvatarImage src={b.picture} /><AvatarFallback className="bg-gradient-to-br from-[#B026FF] to-[#FF0080] text-white text-xs">{b.name?.charAt(0)}</AvatarFallback></Avatar>
                      <div>
                        <p className="font-manrope font-semibold text-sm text-white flex items-center gap-1">
                          {b.name}
                          {b.is_pro_brand && <BadgeCheck className="w-4 h-4 text-purple-400" />}
                          {b.is_hidden && <EyeOff className="w-3 h-3 text-rose-400" />}
                        </p>
                        <p className="text-xs text-white/40">{b.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-sm text-white/60">{b.niche || "—"}</td>
                  <td className="p-3">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className={`h-7 px-2 ${b.is_pro_brand ? "bg-purple-500/20 text-purple-300 hover:bg-purple-500/30" : "bg-white/5 text-white/50 hover:bg-white/10"}`}
                      onClick={() => handleToggleProBrand(b.user_id, b.is_pro_brand)}
                    >
                      {b.is_pro_brand ? (
                        <><BadgeCheck className="w-3 h-3 mr-1" />Pro</>
                      ) : (
                        <><Crown className="w-3 h-3 mr-1" />Make Pro</>
                      )}
                    </Button>
                  </td>
                  <td className="p-3 text-sm font-mono text-white">{fmt(b.requests_sent)}</td>
                  <td className="p-3 text-sm font-mono text-emerald-400">{fmt(b.closed_deals)}</td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className={`h-8 w-8 ${b.is_hidden ? "text-emerald-400" : "text-white/60"} hover:bg-white/10`} onClick={() => toggleUserVisibility(b.user_id)}>
                        {b.is_hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-400 hover:bg-rose-500/10" onClick={() => handleDeleteUser(b.user_id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>
  );

  // --- Pending Brands ---
  const renderPendingBrands = () => (
    <div className="space-y-6">
      <SectionHeader title="Pending Brand Approvals" subtitle={`${pendingBrands.length} awaiting review`} icon={Clock} />
      {pendingBrands.length === 0 ? (
        <GlassCard className="p-16 text-center">
          <CheckCircle className="w-12 h-12 text-emerald-400/50 mx-auto mb-3" />
          <p className="text-white/50">All caught up — no pending approvals</p>
        </GlassCard>
      ) : (
        <div className="space-y-3">
          {pendingBrands.map((b) => (
            <GlassCard key={b.user_id} className="p-5 border-orange-400/20">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-4">
                  <Avatar className="w-14 h-14 border-2 border-orange-400/30"><AvatarImage src={b.picture} /><AvatarFallback className="bg-orange-500/20 text-orange-300 font-bold text-xl">{b.name?.charAt(0)}</AvatarFallback></Avatar>
                  <div>
                    <h3 className="font-manrope font-bold text-white">{b.name}</h3>
                    <p className="text-sm text-white/50">{b.email}</p>
                    <div className="mt-2 space-y-1 text-sm">
                      {b.website_url && <a href={b.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sky-400 hover:underline"><ExternalLink className="w-3 h-3" />{b.website_url}</a>}
                      {b.social_media_url && <><br /><a href={b.social_media_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-pink-400 hover:underline"><Instagram className="w-3 h-3" />{b.social_media_url}</a></>}
                    </div>
                    {b.description && <p className="text-sm text-white/60 mt-2 bg-white/[0.03] p-2 rounded-lg border border-white/5">{b.description}</p>}
                    <p className="text-xs text-white/30 mt-2 font-mono">Applied: {new Date(b.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => handleApproveBrand(b.user_id)} className="bg-gradient-to-r from-[#FF0080] to-[#B026FF] hover:opacity-90 text-white" size="sm"><CheckCircle className="w-4 h-4 mr-1" />Approve</Button>
                  <Button onClick={() => handleRejectBrand(b.user_id)} variant="outline" size="sm" className="bg-rose-500/10 border-rose-400/30 text-rose-300 hover:bg-rose-500/20"><XCircle className="w-4 h-4 mr-1" />Reject</Button>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );

  // --- Deals (NEW) ---
  const renderDeals = () => (
    <div className="space-y-6">
      <SectionHeader title="Deals" subtitle={`${deals.length} total · ${disputedDeals.length} disputed · ${overdueDeals.length} overdue`} icon={Handshake} />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatTile label="Total" value={deals.length} icon={Handshake} />
        <StatTile label="Disputed" value={disputedDeals.length} icon={AlertTriangle} accent="from-orange-500 to-rose-500" />
        <StatTile label="Overdue" value={overdueDeals.length} icon={Clock} accent="from-amber-500 to-orange-500" />
        <StatTile label="Completed" value={deals.filter(d => d.status === "completed").length} icon={CheckCircle} accent="from-emerald-500 to-teal-500" />
      </div>

      {disputedDeals.length > 0 && (
        <div>
          <h3 className="font-manrope font-bold text-white mb-3 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-orange-400" />Disputed Deals ({disputedDeals.length})</h3>
          <div className="space-y-3">
            {disputedDeals.map((d) => (
              <GlassCard key={d.deal_id} className="p-5 border-orange-400/20">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-mono text-xs text-white/40">{d.deal_id}</span>
                      <StatusBadge status={d.status} />
                    </div>
                    <p className="font-manrope text-white"><span className="text-white/50">Brand:</span> {d.brand_name || d.brand_id}</p>
                    <p className="font-manrope text-white"><span className="text-white/50">Star:</span> {d.influencer_name || d.influencer_id}</p>
                    <p className="font-mono text-[#FF4DA6] font-bold text-lg mt-1">{fmtINR(d.amount)}</p>
                    {d.dispute_reason && <p className="text-sm text-white/60 mt-2 italic">"{d.dispute_reason}"</p>}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button size="sm" className="bg-gradient-to-r from-[#FF0080] to-[#B026FF] hover:opacity-90 text-white" onClick={() => handleResolveDispute(d.deal_id, "release_to_influencer")}>
                      Release to Star
                    </Button>
                    <Button size="sm" variant="outline" className="bg-white/5 border-white/10 text-white hover:bg-white/10" onClick={() => handleResolveDispute(d.deal_id, "refund_to_brand")}>
                      Refund Brand
                    </Button>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      )}

      {overdueDeals.length > 0 && (
        <div>
          <h3 className="font-manrope font-bold text-white mb-3 flex items-center gap-2"><Clock className="w-5 h-5 text-amber-400" />Overdue Deals ({overdueDeals.length})</h3>
          <div className="space-y-3">
            {overdueDeals.map((d) => (
              <GlassCard key={d.deal_id} className="p-5 border-amber-400/20">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-white/40">{d.deal_id}</span>
                      <StatusBadge status={d.status} />
                      {d.days_overdue > 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase bg-amber-500/20 text-amber-300 border border-amber-400/30">{d.days_overdue}d overdue</span>}
                    </div>
                    <p className="font-manrope text-white">{d.brand_name} → {d.influencer_name}</p>
                    <p className="font-mono text-[#FF4DA6] font-bold">{fmtINR(d.amount)}</p>
                  </div>
                  <Button size="sm" className="bg-amber-500/20 text-amber-300 border border-amber-400/30 hover:bg-amber-500/30" onClick={() => handleIssueWarning(d.deal_id)}>
                    Issue Warning
                  </Button>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      )}

      <div>
        <h3 className="font-manrope font-bold text-white mb-3">All Deals ({deals.length})</h3>
        <GlassCard className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5 bg-white/[0.02]">
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Deal ID</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Brand</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Star</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Amount</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Status</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Created</th>
                </tr>
              </thead>
              <tbody>
                {deals.map((d) => (
                  <tr key={d.deal_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-3 font-mono text-xs text-white/60">{d.deal_id?.slice(0, 14)}…</td>
                    <td className="p-3 text-sm text-white">{d.brand_name || "—"}</td>
                    <td className="p-3 text-sm text-white">{d.influencer_name || "—"}</td>
                    <td className="p-3 text-sm font-mono text-[#FF4DA6]">{fmtINR(d.amount)}</td>
                    <td className="p-3"><StatusBadge status={d.status} /></td>
                    <td className="p-3 text-xs font-mono text-white/40">{d.created_at ? new Date(d.created_at).toLocaleDateString("en-IN") : "—"}</td>
                  </tr>
                ))}
                {deals.length === 0 && (
                  <tr><td colSpan={6} className="py-12 text-center text-white/40"><Handshake className="w-10 h-10 mx-auto mb-2 opacity-40" /><p>No deals yet</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        </GlassCard>
      </div>
    </div>
  );

  // --- Opportunities ---
  const renderOpportunities = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Opportunities" subtitle="Create gigs for verified creators" icon={Zap}
        right={<Button onClick={() => setShowCreateOpportunity(true)} className="bg-gradient-to-r from-[#FF0080] to-[#B026FF] hover:opacity-90 text-white"><Plus className="w-4 h-4 mr-1" />New</Button>}
      />
      <div className="grid grid-cols-3 gap-4">
        <StatTile label="Active" value={opportunities.filter(o => o.is_active).length} icon={Zap} accent="from-emerald-500 to-teal-500" />
        <StatTile label="Pending Apps" value={pendingApplications.length} icon={Users} accent="from-amber-500 to-orange-500" />
        <StatTile label="Ready for Pay" value={submittedApplications.length} icon={IndianRupee} accent="from-[#FF0080] to-[#B026FF]" />
      </div>

      <div>
        <h3 className="font-manrope font-bold text-white mb-3">Active Opportunities</h3>
        <div className="space-y-3">
          {opportunities.filter(o => o.is_active).length === 0 ? (
            <GlassCard className="p-8 text-center text-white/50">No active opportunities. Click "New" to create one.</GlassCard>
          ) : opportunities.filter(o => o.is_active).map(opp => (
            <GlassCard key={opp.opportunity_id} className="p-4">
              <div className="flex justify-between items-start gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-manrope font-bold text-white">{opp.title}</span>
                    <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">Active</span>
                    {opp.verified_only && <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-400/30">Verified only</span>}
                  </div>
                  <p className="text-sm text-white/60 mb-2">{opp.description}</p>
                  <div className="flex items-center gap-4 text-sm font-mono">
                    <span className="text-emerald-400 font-bold">₹{opp.payment_amount}/{opp.payment_type?.replace("_", " ")}</span>
                    <span className="text-white/40">{opp.platform}</span>
                    <span className="text-white/40">{opp.applications_count || 0} applied</span>
                    <span className="text-white/40">{opp.approved_count || 0} approved</span>
                    <span className="text-white/40">{opp.completed_count || 0} done</span>
                  </div>
                </div>
                <Button size="sm" variant="outline" className="bg-white/5 border-white/10 text-white hover:bg-white/10" onClick={() => toggleOpportunityActive(opp.opportunity_id, true)}>Pause</Button>
              </div>
            </GlassCard>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-manrope font-bold text-white mb-3">Applications to Review ({pendingApplications.length})</h3>
        <div className="space-y-3">
          {pendingApplications.length === 0 ? (
            <GlassCard className="p-6 text-center text-white/50">No pending applications</GlassCard>
          ) : pendingApplications.map(app => (
            <GlassCard key={app.application_id} className="p-4 border-amber-400/20">
              <div className="flex justify-between items-start gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-manrope font-semibold text-white">{app.talent_name}</span>
                    {app.talent_verified && <BadgeCheck className="w-4 h-4 text-sky-400" />}
                    <span className="text-sm text-white/50 font-mono">{fmt(app.talent_followers)} followers</span>
                  </div>
                  <p className="text-sm text-white/60">Applied for: <strong className="text-white">{app.opportunity_title}</strong></p>
                  <p className="text-xs text-white/40 font-mono mt-1">₹{app.payment_amount} · {app.platform}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-gradient-to-r from-[#FF0080] to-[#B026FF] text-white" onClick={() => handleApplicationAction(app.application_id, "approve")}>Approve</Button>
                  <Button size="sm" variant="outline" className="bg-rose-500/10 border-rose-400/30 text-rose-300 hover:bg-rose-500/20" onClick={() => handleApplicationAction(app.application_id, "reject")}>Reject</Button>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-manrope font-bold text-white mb-3">Ready for Payment ({submittedApplications.length})</h3>
        <div className="space-y-3">
          {submittedApplications.length === 0 ? (
            <GlassCard className="p-6 text-center text-white/50">No submissions waiting</GlassCard>
          ) : submittedApplications.map(app => (
            <GlassCard key={app.application_id} className="p-4 border-emerald-400/20">
              <div className="flex justify-between items-start gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-manrope font-semibold text-white">{app.talent_name}</span>
                    <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-300 border border-sky-400/30">Submitted</span>
                  </div>
                  <p className="text-sm text-white/60">{app.opportunity_title}</p>
                  {app.submission_link && <a href={app.submission_link} target="_blank" rel="noreferrer" className="text-sm text-sky-400 hover:underline">View submission →</a>}
                </div>
                <Button size="sm" className="bg-gradient-to-r from-[#FF0080] to-[#B026FF] text-white" onClick={() => handleApplicationAction(app.application_id, "mark-complete")}>
                  <IndianRupee className="w-4 h-4 mr-1" />Release ₹{app.payment_amount}
                </Button>
              </div>
            </GlassCard>
          ))}
        </div>
      </div>
    </div>
  );

  // --- Withdrawals ---
  const renderWithdrawals = () => (
    <div className="space-y-6">
      <SectionHeader title="Withdrawals" subtitle={`${pendingWithdrawals.length} pending · ${withdrawals.length} total`} icon={Wallet} />
      {withdrawals.length === 0 ? (
        <GlassCard className="p-16 text-center"><Wallet className="w-12 h-12 text-white/20 mx-auto mb-3" /><p className="text-white/50">No withdrawal requests yet</p></GlassCard>
      ) : (
        <GlassCard className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5 bg-white/[0.02]">
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">User</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Amount</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Method</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Payment Info</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Status</th>
                  <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Actions</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.map((w) => (
                  <tr key={w.withdrawal_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-3">
                      <p className="font-manrope text-sm text-white">{w.user_name}</p>
                      <p className="text-xs text-white/40">{w.user_email}</p>
                    </td>
                    <td className="p-3 font-mono font-bold text-[#FF4DA6]">{fmtINR(w.amount)}</td>
                    <td className="p-3">
                      <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full ${w.payment_method === "upi" ? "bg-purple-500/20 text-purple-300 border border-purple-400/30" : "bg-sky-500/20 text-sky-300 border border-sky-400/30"}`}>{w.payment_method === "upi" ? "UPI" : "Bank"}</span>
                    </td>
                    <td className="p-3 text-xs">
                      {w.payment_method === "upi" ? (
                        <span className="font-mono bg-black/30 px-2 py-1 rounded text-white/70">{w.payment_info?.upi_id}</span>
                      ) : (
                        <div className="space-y-0.5 font-mono text-white/60">
                          <p>{w.payment_info?.bank_account_name}</p>
                          <p>A/C: {w.payment_info?.bank_account_number}</p>
                          <p>IFSC: {w.payment_info?.bank_ifsc}</p>
                        </div>
                      )}
                    </td>
                    <td className="p-3"><StatusBadge status={w.status} /></td>
                    <td className="p-3">
                      {w.status === "pending" ? (
                        <div className="flex gap-2">
                          <Button size="sm" className="bg-gradient-to-r from-[#FF0080] to-[#B026FF] text-white h-8" onClick={() => handleProcessWithdrawal(w.withdrawal_id, "approve")} disabled={processing}><CheckCircle className="w-4 h-4 mr-1" />Paid</Button>
                          <Button size="sm" variant="outline" className="bg-rose-500/10 border-rose-400/30 text-rose-300 h-8" onClick={() => handleProcessWithdrawal(w.withdrawal_id, "reject")} disabled={processing}><XCircle className="w-4 h-4" /></Button>
                        </div>
                      ) : <span className="text-xs text-white/30 font-mono uppercase">Processed</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>
      )}
    </div>
  );

  // --- Push Notifications ---
  const handlePushAudienceChange = async (newAudience) => {
    setPushForm((f) => ({ ...f, audience: newAudience, user_id: newAudience === "user" ? f.user_id : "" }));
    if (newAudience === "user") {
      setPushReach(null);
      return;
    }
    try {
      const r = await axios.get(`${API}/admin/push/tokens`, {
        headers: getAdminHeaders(),
        params: { audience: newAudience, limit: 1, skip: 0 },
      });
      setPushReach(r.data?.total ?? 0);
    } catch (e) {
      setPushReach(null);
    }
  };

  const fetchPushHistory = async () => {
    try {
      const r = await axios.get(`${API}/admin/push/broadcasts`, { headers: getAdminHeaders(), params: { limit: 20 } });
      setPushHistory(r.data?.items || []);
    } catch (e) {
      setPushHistory([]);
    }
  };

  const handlePushUserSearch = async (q) => {
    setPushUserSearch(q);
    if (!q || q.trim().length < 2) {
      setPushUserResults([]);
      return;
    }
    try {
      const r = await axios.get(`${API}/admin/users`, { headers: getAdminHeaders() });
      const all = r.data || [];
      const needle = q.toLowerCase();
      setPushUserResults(
        all
          .filter((u) => u.user_type !== "admin")
          .filter((u) =>
            (u.name || "").toLowerCase().includes(needle) ||
            (u.email || "").toLowerCase().includes(needle) ||
            (u.user_id || "").toLowerCase().includes(needle)
          )
          .slice(0, 10)
      );
    } catch (e) {
      setPushUserResults([]);
    }
  };

  const handlePushPickUser = (u) => {
    setPushUserPicked(u);
    setPushForm((f) => ({ ...f, user_id: u.user_id }));
    setPushUserSearch(`${u.name || u.email} (${u.user_type})`);
    setPushUserResults([]);
    setPushReach(1);
  };

  const handleSendPush = async (e) => {
    e?.preventDefault?.();
    if (!pushForm.title.trim() || !pushForm.body.trim()) {
      toast.error("Title and message are required");
      return;
    }
    if (pushForm.audience === "user" && !pushForm.user_id) {
      toast.error("Pick a specific user first");
      return;
    }
    const deepLink = pushForm.deep_link === "custom" ? pushForm.custom_deep_link.trim() : pushForm.deep_link;
    setPushSending(true);
    try {
      const r = await axios.post(
        `${API}/admin/push/broadcast`,
        {
          audience: pushForm.audience,
          user_id: pushForm.audience === "user" ? pushForm.user_id : undefined,
          title: pushForm.title.trim(),
          body: pushForm.body.trim(),
          deep_link: deepLink || undefined,
        },
        { headers: getAdminHeaders() }
      );
      const { sent, failed, removed, target_count } = r.data || {};
      toast.success(`Sent to ${sent || 0} of ${target_count || 0} devices · ${removed || 0} inactive removed${failed ? ` · ${failed} failed` : ""}`);
      setPushForm({ audience: "all", user_id: "", title: "", body: "", deep_link: "", custom_deep_link: "" });
      setPushUserSearch("");
      setPushUserPicked(null);
      setPushReach(null);
      fetchPushHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send notification");
    } finally {
      setPushSending(false);
    }
  };

  const renderNotifications = () => {
    const titleMax = 60;
    const bodyMax = 160;
    const deepLinkOptions = [
      { v: "", label: "(none)" },
      { v: "/home", label: "/home" },
      { v: "/deals", label: "/deals" },
      { v: "/messages", label: "/messages" },
      { v: "/notifications", label: "/notifications" },
      { v: "/discover", label: "/discover" },
      { v: "custom", label: "Custom..." },
    ];
    return (
      <div className="space-y-6">
        <SectionHeader
          title="Send Push Notification"
          subtitle="Reach app users on iOS / Android via Expo"
          icon={Bell}
        />

        <GlassCard className="p-6">
          <form onSubmit={handleSendPush} className="space-y-5">
            {/* Audience */}
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Audience</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                {[
                  { v: "all", label: "All", icon: Users },
                  { v: "stars", label: "Stars only", icon: Star },
                  { v: "brands", label: "Brands only", icon: Briefcase },
                  { v: "user", label: "Specific user", icon: User },
                ].map((opt) => {
                  const Icon = opt.icon;
                  const active = pushForm.audience === opt.v;
                  return (
                    <button
                      type="button"
                      key={opt.v}
                      onClick={() => handlePushAudienceChange(opt.v)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition ${
                        active
                          ? "bg-gradient-to-r from-[#FF0080]/20 to-[#B026FF]/20 border-[#FF0080]/50 text-white"
                          : "bg-white/[0.03] border-white/10 text-white/60 hover:text-white hover:border-white/20"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* User picker */}
            {pushForm.audience === "user" && (
              <div className="relative">
                <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Search user</Label>
                <Input
                  placeholder="Name, email or user_id…"
                  value={pushUserSearch}
                  onChange={(e) => {
                    handlePushUserSearch(e.target.value);
                    setPushUserPicked(null);
                    setPushForm((f) => ({ ...f, user_id: "" }));
                  }}
                  className="bg-black/30 border-white/10 text-white rounded-lg mt-1"
                />
                {pushUserResults.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-[#11101A] border border-white/10 rounded-lg max-h-72 overflow-y-auto shadow-2xl">
                    {pushUserResults.map((u) => (
                      <button
                        type="button"
                        key={u.user_id}
                        onClick={() => handlePushPickUser(u)}
                        className="w-full text-left px-3 py-2 hover:bg-white/[0.05] border-b border-white/5 last:border-0"
                      >
                        <div className="text-sm text-white">{u.name || "(no name)"}</div>
                        <div className="text-[11px] text-white/40 font-mono">
                          {u.email} · {u.user_type}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {pushUserPicked && (
                  <div className="mt-2 text-xs text-emerald-400 font-mono">
                    ✓ Selected: {pushUserPicked.name || pushUserPicked.email} ({pushUserPicked.user_id})
                  </div>
                )}
              </div>
            )}

            {/* Title */}
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-white/50">
                Title{" "}
                <span className={`ml-2 ${pushForm.title.length > titleMax ? "text-rose-400" : "text-white/30"}`}>
                  {pushForm.title.length}/{titleMax}
                </span>
              </Label>
              <Input
                placeholder="🎉 New campaign live!"
                value={pushForm.title}
                onChange={(e) => setPushForm({ ...pushForm, title: e.target.value.slice(0, titleMax) })}
                className="bg-black/30 border-white/10 text-white rounded-lg mt-1"
              />
            </div>

            {/* Body */}
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-white/50">
                Message{" "}
                <span className={`ml-2 ${pushForm.body.length > bodyMax ? "text-rose-400" : "text-white/30"}`}>
                  {pushForm.body.length}/{bodyMax}
                </span>
              </Label>
              <Textarea
                placeholder="Tap to claim your offer before it expires."
                value={pushForm.body}
                onChange={(e) => setPushForm({ ...pushForm, body: e.target.value.slice(0, bodyMax) })}
                rows={3}
                className="bg-black/30 border-white/10 text-white rounded-lg mt-1"
              />
            </div>

            {/* Deep link */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Deep link (optional)</Label>
                <select
                  value={pushForm.deep_link}
                  onChange={(e) => setPushForm({ ...pushForm, deep_link: e.target.value })}
                  className="w-full mt-1 bg-black/30 border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF0080]/50"
                >
                  {deepLinkOptions.map((o) => (
                    <option key={o.v} value={o.v} className="bg-[#11101A]">{o.label}</option>
                  ))}
                </select>
              </div>
              {pushForm.deep_link === "custom" && (
                <div>
                  <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Custom path</Label>
                  <Input
                    placeholder="/chat/abc123"
                    value={pushForm.custom_deep_link}
                    onChange={(e) => setPushForm({ ...pushForm, custom_deep_link: e.target.value })}
                    className="bg-black/30 border-white/10 text-white rounded-lg mt-1"
                  />
                </div>
              )}
            </div>

            {/* Reach + submit */}
            <div className="flex items-center justify-between pt-2 border-t border-white/5">
              <div className="text-sm text-white/60">
                {pushReach == null ? (
                  <span className="font-mono text-white/30">— estimating reach —</span>
                ) : (
                  <span>
                    Will reach approx{" "}
                    <span className="font-bold text-white">{fmt(pushReach)}</span>{" "}
                    device{pushReach === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              <Button
                type="submit"
                disabled={pushSending || !pushForm.title.trim() || !pushForm.body.trim()}
                className="bg-gradient-to-r from-[#FF0080] to-[#B026FF] text-white px-6"
              >
                {pushSending ? "Sending…" : (<><Send className="w-4 h-4 mr-2" />Send Notification</>)}
              </Button>
            </div>
          </form>
        </GlassCard>

        {/* History */}
        <GlassCard className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-manrope font-bold text-white flex items-center gap-2">
              <Clock className="w-4 h-4 text-white/60" />
              Recent broadcasts
            </h3>
            <Button size="sm" variant="ghost" onClick={fetchPushHistory} className="text-white/60 hover:text-white">
              Refresh
            </Button>
          </div>
          {pushHistory.length === 0 ? (
            <p className="text-sm text-white/40 text-center py-6">No broadcasts yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/5 bg-white/[0.02]">
                    <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">When</th>
                    <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Audience</th>
                    <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Title</th>
                    <th className="text-center p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Sent</th>
                    <th className="text-center p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {pushHistory.map((b) => (
                    <tr key={b.broadcast_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="p-3 text-xs font-mono text-white/50">
                        {b.created_at ? new Date(b.created_at).toLocaleString() : "—"}
                      </td>
                      <td className="p-3">
                        <span className="text-xs font-mono px-2 py-0.5 rounded bg-white/5 text-white/70">
                          {b.audience}{b.audience === "user" && b.user_id ? `: ${b.user_id.slice(0, 8)}…` : ""}
                        </span>
                      </td>
                      <td className="p-3 text-white/90 truncate max-w-[280px]" title={b.title}>{b.title}</td>
                      <td className="p-3 text-center font-mono text-emerald-400">{b.sent ?? 0}</td>
                      <td className="p-3 text-center font-mono text-rose-400">{b.failed ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </GlassCard>
      </div>
    );
  };

  // --- Promo Codes ---
  const renderPromoCodes = () => (
    <div className="space-y-6">
      <SectionHeader title="Promo Codes" subtitle={`${promoCodes.length} active codes`} icon={Tag} />

      <GlassCard className="p-5">
        <h3 className="font-manrope font-bold text-white mb-4 flex items-center gap-2"><Plus className="w-4 h-4 text-[#FF4DA6]" />Create New Promo Code</h3>
        <form onSubmit={handleCreatePromoCode} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div><Label className="text-xs text-white/60">Code *</Label><Input placeholder="ROHAN50" value={newPromoForm.code} onChange={(e) => setNewPromoForm({ ...newPromoForm, code: e.target.value.toUpperCase() })} className="bg-black/30 border-white/10 text-white rounded-lg mt-1" /></div>
          <div><Label className="text-xs text-white/60">Advertiser Name *</Label><Input placeholder="Rohan Sharma" value={newPromoForm.advertiser_name} onChange={(e) => setNewPromoForm({ ...newPromoForm, advertiser_name: e.target.value })} className="bg-black/30 border-white/10 text-white rounded-lg mt-1" /></div>
          <div><Label className="text-xs text-white/60">Contact *</Label><Input placeholder="9876543210" value={newPromoForm.advertiser_contact} onChange={(e) => setNewPromoForm({ ...newPromoForm, advertiser_contact: e.target.value })} className="bg-black/30 border-white/10 text-white rounded-lg mt-1" /></div>
          <div><Label className="text-xs text-white/60">Discount %</Label><Input type="number" value={newPromoForm.discount_percent} onChange={(e) => setNewPromoForm({ ...newPromoForm, discount_percent: parseInt(e.target.value) || 0 })} className="bg-black/30 border-white/10 text-white rounded-lg mt-1" /></div>
          <div><Label className="text-xs text-white/60">Commission (₹)</Label><Input type="number" value={newPromoForm.commission_per_use} onChange={(e) => setNewPromoForm({ ...newPromoForm, commission_per_use: parseInt(e.target.value) || 0 })} className="bg-black/30 border-white/10 text-white rounded-lg mt-1" /></div>
          <div className="flex items-end"><Button type="submit" disabled={creatingPromo} className="w-full bg-gradient-to-r from-[#FF0080] to-[#B026FF] text-white">{creatingPromo ? "Creating..." : "Create Code"}</Button></div>
        </form>
      </GlassCard>

      {promoCodes.length === 0 ? (
        <GlassCard className="p-12 text-center"><Tag className="w-10 h-10 text-white/20 mx-auto mb-3" /><p className="text-white/50">No promo codes yet</p></GlassCard>
      ) : (
        <GlassCard className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-white/5 bg-white/[0.02]">
                <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Code</th>
                <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Advertiser</th>
                <th className="text-left p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Contact</th>
                <th className="text-center p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Uses</th>
                <th className="text-center p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Earnings</th>
                <th className="text-center p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Pending</th>
                <th className="text-center p-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Actions</th>
              </tr></thead>
              <tbody>
                {promoCodes.map((p) => (
                  <tr key={p.promo_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-3"><span className="font-mono font-bold text-[#B026FF] bg-[#B026FF]/10 border border-[#B026FF]/20 px-2 py-1 rounded">{p.code}</span><span className="ml-2 text-[10px] font-mono text-white/40">{p.discount_percent}% off</span></td>
                    <td className="p-3 font-manrope text-sm text-white">{p.advertiser_name}</td>
                    <td className="p-3 text-xs font-mono text-white/50">{p.advertiser_contact}</td>
                    <td className="p-3 text-center"><span className="font-mono text-sm text-sky-400">{p.total_uses}</span></td>
                    <td className="p-3 text-center font-mono font-bold text-emerald-400">{fmtINR(p.total_earnings)}</td>
                    <td className="p-3 text-center">{p.pending_payout > 0 ? <span className="font-mono text-amber-400">{fmtINR(p.pending_payout)}</span> : <span className="text-white/20 font-mono">₹0</span>}</td>
                    <td className="p-3 text-center">
                      <div className="flex gap-1 justify-center">
                        {p.pending_payout > 0 && <Button size="sm" variant="ghost" className="text-emerald-400 hover:bg-emerald-500/10 h-8" onClick={() => handleMarkPromoPaid(p.promo_id, p.pending_payout)}><CheckCircle className="w-4 h-4 mr-1" />Paid</Button>}
                        <Button size="sm" variant="ghost" className="text-rose-400 hover:bg-rose-500/10 h-8" onClick={() => handleDeletePromoCode(p.promo_id)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>
      )}
    </div>
  );

  // --- Collab Card Generator ---
  const collabCardRef = useRef(null);
  
  const downloadCollabCard = async () => {
    if (!collabCardRef.current || !selectedBrandForCard || !selectedStarForCard) {
      toast.error("Please select both Brand and Star");
      return;
    }
    setGeneratingCard(true);
    try {
      // Wait a bit for images to load
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const canvas = await html2canvas(collabCardRef.current, {
        backgroundColor: null,
        scale: 3,
        useCORS: true,
        allowTaint: false,
        logging: false,
        imageTimeout: 5000,
      });
      const link = document.createElement("a");
      link.download = `deal-completed-${(selectedStarForCard.name || "star").replace(/\s+/g, "_")}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Collab card downloaded!");
    } catch (e) {
      console.error(e);
      toast.error("Download failed. Try again.");
    } finally {
      setGeneratingCard(false);
    }
  };

  // Helper to get proper image URL
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/')) return `${API.replace('/api', '')}${url}`;
    if (url.startsWith('/')) return `${API.replace('/api', '')}${url}`;
    return `${API}/files/${url}`;
  };

  const renderCollabCardGenerator = () => (
    <div className="space-y-6">
      <SectionHeader title="Collab Card Generator" subtitle="Create promotional collaboration cards" icon={Image} />
      
      <GlassCard className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Selection Panel */}
          <div className="space-y-6">
            <div>
              <Label className="text-white/70 mb-2 block">Select Brand</Label>
              <Select 
                value={selectedBrandForCard?.user_id || ""} 
                onValueChange={(val) => {
                  const brand = brands.find(b => b.user_id === val);
                  setSelectedBrandForCard(brand);
                }}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Choose a brand..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a2e] border-white/10">
                  {brands.map((b) => (
                    <SelectItem key={b.user_id} value={b.user_id} className="text-white hover:bg-white/10">
                      <div className="flex items-center gap-2">
                        <Avatar className="w-6 h-6">
                          <AvatarImage src={getImageUrl(b.picture)} />
                          <AvatarFallback className="bg-purple-500/20 text-purple-300 text-xs">{b.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        {b.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-white/70 mb-2 block">Select Star/Creator</Label>
              <Select 
                value={selectedStarForCard?.user_id || ""} 
                onValueChange={(val) => {
                  const star = talents.find(t => t.user_id === val);
                  setSelectedStarForCard(star);
                }}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Choose a star..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a2e] border-white/10 max-h-[300px]">
                  {talents.map((t) => (
                    <SelectItem key={t.user_id} value={t.user_id} className="text-white hover:bg-white/10">
                      <div className="flex items-center gap-2">
                        <Avatar className="w-6 h-6">
                          <AvatarImage src={getImageUrl(t.picture || t.photo_url)} />
                          <AvatarFallback className="bg-pink-500/20 text-pink-300 text-xs">{t.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        {t.name}
                        {t.is_verified && <BadgeCheck className="w-4 h-4 text-purple-400" />}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button 
              onClick={downloadCollabCard} 
              disabled={!selectedBrandForCard || !selectedStarForCard || generatingCard}
              className="w-full bg-gradient-to-r from-[#FF0080] to-[#B026FF] hover:opacity-90"
            >
              {generatingCard ? (
                <><span className="animate-spin mr-2">⏳</span> Generating...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Download Card</>
              )}
            </Button>
          </div>

          {/* Preview Panel */}
          <div className="flex items-center justify-center">
            <div 
              ref={collabCardRef}
              className="relative rounded-2xl overflow-hidden w-full max-w-[350px]"
              style={{
                background: "linear-gradient(135deg, #1E293B 0%, #3B1361 50%, #1E293B 100%)",
              }}
            >
              {/* Sparkle decorations */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {[...Array(8)].map((_, i) => (
                  <Sparkles
                    key={i}
                    className="absolute text-yellow-300/30"
                    style={{
                      top: `${10 + Math.random() * 80}%`,
                      left: `${10 + Math.random() * 80}%`,
                      width: 10 + Math.random() * 6,
                      height: 10 + Math.random() * 6,
                    }}
                  />
                ))}
              </div>

              <div className="relative z-10 p-6">
                {/* Header */}
                <div className="text-center mb-6">
                  <div 
                    className="inline-flex items-center justify-center gap-2 text-white px-5 py-2.5 rounded-full text-sm font-bold mb-3 tracking-wide"
                    style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #F97316 100%)' }}
                  >
                    <Trophy className="w-4 h-4" />
                    DEAL COMPLETED
                  </div>
                  <h2 className="text-white font-bold text-xl">Collaboration Success!</h2>
                </div>

                {/* Brand & Star Connection */}
                <div className="flex items-center justify-center gap-6 mb-8">
                  {/* Brand Photo */}
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full p-1" style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #A855F7 100%)' }}>
                      <div 
                        className="w-full h-full rounded-full overflow-hidden flex items-center justify-center relative"
                        style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)' }}
                      >
                        {/* Fallback Initial - always visible behind image */}
                        <span className="text-white text-3xl font-bold absolute inset-0 flex items-center justify-center z-0">
                          {(selectedBrandForCard?.name || "B").charAt(0).toUpperCase()}
                        </span>
                        {/* Image - will cover fallback if loads successfully */}
                        {getImageUrl(selectedBrandForCard?.picture) && (
                          <img 
                            src={getImageUrl(selectedBrandForCard?.picture)} 
                            alt="Brand" 
                            className="w-full h-full object-cover absolute inset-0 z-10" 
                          />
                        )}
                      </div>
                    </div>
                    <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-[11px] px-4 py-1.5 rounded-full font-bold whitespace-nowrap shadow-lg">
                      Brand
                    </div>
                  </div>

                  {/* Connection Icon */}
                  <div className="flex flex-col items-center">
                    <Handshake className="w-10 h-10 text-yellow-400" />
                  </div>

                  {/* Star Photo */}
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full p-1" style={{ background: 'linear-gradient(135deg, #EC4899 0%, #F472B6 50%, #93C5FD 100%)' }}>
                      <div 
                        className="w-full h-full rounded-full overflow-hidden flex items-center justify-center relative"
                        style={{ background: 'linear-gradient(135deg, #DB2777 0%, #9333EA 100%)' }}
                      >
                        {/* Fallback Initial - always visible behind image */}
                        <span className="text-white text-3xl font-bold absolute inset-0 flex items-center justify-center z-0">
                          {(selectedStarForCard?.name || "S").charAt(0).toUpperCase()}
                        </span>
                        {/* Image - will cover fallback if loads successfully */}
                        {getImageUrl(selectedStarForCard?.picture || selectedStarForCard?.photo_url) && (
                          <img 
                            src={getImageUrl(selectedStarForCard?.picture || selectedStarForCard?.photo_url)} 
                            alt="Star" 
                            className="w-full h-full object-cover absolute inset-0 z-10" 
                          />
                        )}
                      </div>
                    </div>
                    <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-[#7B2FBE] text-white text-[11px] px-3 py-1.5 rounded-full font-bold whitespace-nowrap flex items-center gap-1 shadow-lg">
                      <BadgeCheck className="w-3.5 h-3.5" />
                      Pro Creator
                    </div>
                  </div>
                </div>

                {/* Star Name & Info */}
                <div className="text-center mb-4">
                  <h3 className="text-white font-bold text-xl flex items-center justify-center gap-2">
                    {selectedStarForCard?.name || "Select a Star"}
                    <BadgeCheck className="w-5 h-5 text-[#87CEEB]" />
                  </h3>
                  <p className="text-white/70 text-sm mt-1">Pro Creator</p>
                  {selectedBrandForCard && (
                    <p className="text-white/50 text-sm mt-2">
                      worked with <span className="text-white/80 font-semibold">{selectedBrandForCard.name}</span>
                    </p>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-center gap-2 pt-4 border-t border-white/10">
                  <Star className="w-4 h-4 text-[#FF69B4]" />
                  <span className="text-white/60 text-xs font-medium">FOUNDSTAR</span>
                  <span className="text-white/40 text-xs">• Where Brands Find Their Star</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </GlassCard>
    </div>
  );

  // --- Insta Content Generator ---
  const instaCardRef = useRef(null);
  
  const indianFemaleNames = [
    "Priya Sharma", "Ananya Patel", "Sneha Reddy", "Pooja Verma", "Riya Singh",
    "Ishita Gupta", "Kavya Nair", "Aisha Khan", "Meera Joshi", "Tanvi Deshmukh",
    "Shruti Kulkarni", "Aditi Rao", "Neha Kapoor", "Divya Menon", "Sakshi Agarwal",
    "Nikita Jain", "Simran Kaur", "Trisha Banerjee", "Kiara Malhotra", "Anushka Dubey",
    "Isha Tiwari", "Mira Sinha", "Ridhi Chopra", "Palak Arora", "Nisha Pandey"
  ];
  
  const indianMaleNames = [
    "Aarav Malhotra", "Vihaan Choudhary", "Arjun Saxena", "Kabir Bhatia", "Reyansh Pillai",
    "Aditya Iyer", "Vivaan Thakur", "Dhruv Shetty", "Rohan Khanna", "Arnav Mehta",
    "Siddharth Kapoor", "Raj Singhania", "Vikram Desai", "Karan Oberoi", "Yash Goyal",
    "Aniket Sharma", "Pranav Reddy", "Harsh Agarwal", "Ritvik Nair", "Dev Patel"
  ];

  const indianNames = [...indianFemaleNames, ...indianMaleNames];
  
  const categories = [
    "Fashion & Lifestyle", "Beauty & Makeup", "Food & Travel", "Fitness & Health",
    "Tech & Gaming", "Comedy & Entertainment", "Music & Dance", "Photography"
  ];

  // Stock profile photos for demo (CORS-friendly unsplash URLs)
  const stockProfilePhotos = {
    female: [
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop&crop=face",
      "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&h=400&fit=crop&crop=face",
      "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=400&h=400&fit=crop&crop=face",
      "https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=400&h=400&fit=crop&crop=face",
      "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=400&fit=crop&crop=face",
      "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=400&h=400&fit=crop&crop=face",
    ],
    male: [
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face",
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop&crop=face",
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face",
      "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&h=400&fit=crop&crop=face",
      "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop&crop=face",
      "https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?w=400&h=400&fit=crop&crop=face",
    ]
  };

  // Stock portfolio images for demo
  const stockPortfolioImages = [
    "https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=300&h=300&fit=crop",
    "https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=300&h=300&fit=crop",
    "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&h=300&fit=crop",
    "https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=300&h=300&fit=crop",
    "https://images.unsplash.com/photo-1485462537746-965f33f7f6a7?w=300&h=300&fit=crop",
    "https://images.unsplash.com/photo-1509631179647-0177331693ae?w=300&h=300&fit=crop",
    "https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=300&h=300&fit=crop",
    "https://images.unsplash.com/photo-1502716119720-b23a93e5fe1b?w=300&h=300&fit=crop",
    "https://images.unsplash.com/photo-1483985988355-763728e1935b?w=300&h=300&fit=crop",
    "https://images.unsplash.com/photo-1529139574466-a303027c1d8b?w=300&h=300&fit=crop",
    "https://images.unsplash.com/photo-1581044777550-4cfa60707c03?w=300&h=300&fit=crop",
    "https://images.unsplash.com/photo-1512436991641-6745cdb1723f?w=300&h=300&fit=crop",
  ];

  const generateRandomName = () => {
    const name = indianNames[Math.floor(Math.random() * indianNames.length)];
    setDemoCreatorName(name);
  };

  // ONE-CLICK MAGIC: Generate complete fake creator instantly
  const generateMagicCreator = () => {
    // Randomly pick male or female
    const isFemale = Math.random() > 0.4; // 60% female bias for fashion/lifestyle
    
    // Pick random name based on gender
    const nameList = isFemale ? indianFemaleNames : indianMaleNames;
    const randomName = nameList[Math.floor(Math.random() * nameList.length)];
    
    // Pick random category
    const randomCategory = categories[Math.floor(Math.random() * categories.length)];
    
    // Pick random profile photo based on gender
    const photoList = isFemale ? stockProfilePhotos.female : stockProfilePhotos.male;
    const randomPhoto = photoList[Math.floor(Math.random() * photoList.length)];
    
    // Pick 2-4 random portfolio images
    const shuffledPortfolio = [...stockPortfolioImages].sort(() => Math.random() - 0.5);
    const portfolioCount = 2 + Math.floor(Math.random() * 3); // 2-4 images
    const randomPortfolio = shuffledPortfolio.slice(0, portfolioCount);
    
    // Set all states
    setDemoCreatorName(randomName);
    setDemoCreatorCategory(randomCategory);
    setDemoCreatorPhoto(randomPhoto);
    setDemoPortfolioImages(randomPortfolio);
    setSelectedCreatorForInsta(null); // Ensure demo mode
    
    toast.success(`✨ ${randomName} generated!`);
  };

  const handlePortfolioUpload = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setDemoPortfolioImages(prev => [...prev.slice(-3), reader.result]); // Keep max 4
      };
      reader.readAsDataURL(file);
    });
  };

  const downloadInstaCard = async () => {
    if (!instaCardRef.current) return;
    setGeneratingInstaCard(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 300));
      const canvas = await html2canvas(instaCardRef.current, {
        backgroundColor: null,
        scale: 3,
        useCORS: true,
        allowTaint: false,
      });
      const link = document.createElement("a");
      const name = selectedCreatorForInsta?.name || demoCreatorName || "creator";
      link.download = `new-pro-creator-${name.replace(/\s+/g, "_")}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Instagram card downloaded!");
    } catch (e) {
      console.error(e);
      toast.error("Download failed");
    } finally {
      setGeneratingInstaCard(false);
    }
  };

  const getCreatorPhoto = () => {
    if (demoCreatorPhoto) return demoCreatorPhoto;
    if (selectedCreatorForInsta) {
      return getImageUrl(selectedCreatorForInsta.picture || selectedCreatorForInsta.photo_url);
    }
    return null;
  };

  const getCreatorName = () => {
    return selectedCreatorForInsta?.name || demoCreatorName || "Creator Name";
  };

  const getCreatorCategory = () => {
    return selectedCreatorForInsta?.category || demoCreatorCategory;
  };

  // ==================== QUICK ONBOARD SECTION ====================
  const handleQuickOnboard = async (e) => {
    e.preventDefault();
    if (!quickOnboardForm.name || !quickOnboardForm.phone) {
      toast.error("Name and phone are required");
      return;
    }
    
    // Validate phone (10 digits)
    const phone = quickOnboardForm.phone.replace(/\D/g, '');
    if (phone.length !== 10) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }
    
    setQuickOnboardLoading(true);
    try {
      const res = await axios.post(`${API}/admin/quick-create-star`, {
        ...quickOnboardForm,
        phone: phone
      }, { headers: getAdminHeaders() });
      
      setQuickOnboardResult(res.data);
      toast.success(`Star profile created for ${quickOnboardForm.name}!`);
      
      // Reset form
      setQuickOnboardForm({
        name: "",
        phone: "",
        instagram_handle: "",
        category: "influencers",
        city: ""
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create profile");
    } finally {
      setQuickOnboardLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const renderQuickOnboard = () => (
    <div className="space-y-6">
      <SectionHeader title="Quick Onboard" subtitle="Create Star profiles in 30 seconds and share payment links" icon={UserPlus} />
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Section */}
        <GlassCard className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            Create New Star
          </h3>
          
          <form onSubmit={handleQuickOnboard} className="space-y-4">
            <div>
              <Label className="text-white/60 text-sm">Name *</Label>
              <Input
                value={quickOnboardForm.name}
                onChange={(e) => setQuickOnboardForm({...quickOnboardForm, name: e.target.value})}
                placeholder="Enter creator name"
                className="bg-white/5 border-white/10 text-white mt-1"
                required
              />
            </div>
            
            <div>
              <Label className="text-white/60 text-sm">Phone Number *</Label>
              <div className="flex mt-1">
                <span className="px-3 py-2 bg-white/10 border border-white/10 border-r-0 rounded-l-md text-white/60 text-sm">+91</span>
                <Input
                  value={quickOnboardForm.phone}
                  onChange={(e) => setQuickOnboardForm({...quickOnboardForm, phone: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                  placeholder="9876543210"
                  className="bg-white/5 border-white/10 text-white rounded-l-none"
                  required
                />
              </div>
            </div>
            
            <div>
              <Label className="text-white/60 text-sm">Instagram Handle</Label>
              <div className="flex mt-1">
                <span className="px-3 py-2 bg-white/10 border border-white/10 border-r-0 rounded-l-md text-white/60 text-sm">@</span>
                <Input
                  value={quickOnboardForm.instagram_handle}
                  onChange={(e) => setQuickOnboardForm({...quickOnboardForm, instagram_handle: e.target.value.replace('@', '')})}
                  placeholder="instagram_handle"
                  className="bg-white/5 border-white/10 text-white rounded-l-none"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-white/60 text-sm">Category</Label>
              <select
                value={quickOnboardForm.category}
                onChange={(e) => setQuickOnboardForm({...quickOnboardForm, category: e.target.value})}
                className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
              >
                <option value="influencers">Influencers</option>
                <option value="models">Models</option>
                <option value="actors">Actors</option>
                <option value="dancers">Dancers</option>
                <option value="photographers">Photographers</option>
                <option value="editors">Editors</option>
                <option value="gaming">Gaming</option>
                <option value="content_writers">Content Writers</option>
                <option value="voice_artists">Voice Artists</option>
              </select>
            </div>
            
            <div>
              <Label className="text-white/60 text-sm">City</Label>
              <Input
                value={quickOnboardForm.city}
                onChange={(e) => setQuickOnboardForm({...quickOnboardForm, city: e.target.value})}
                placeholder="Mumbai, Delhi, etc."
                className="bg-white/5 border-white/10 text-white mt-1"
              />
            </div>
            
            <Button
              type="submit"
              disabled={quickOnboardLoading}
              className="w-full bg-gradient-to-r from-[#FF0080] to-[#B026FF] hover:from-[#FF0080]/90 hover:to-[#B026FF]/90 text-white py-6"
            >
              {quickOnboardLoading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Create & Generate Link
                </span>
              )}
            </Button>
          </form>
        </GlassCard>

        {/* Result Section */}
        <GlassCard className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Share2 className="w-5 h-5 text-emerald-400" />
            Share Links
          </h3>
          
          {quickOnboardResult ? (
            <div className="space-y-4">
              {/* Success Header */}
              <div className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
                <div>
                  <p className="font-semibold text-white">{quickOnboardResult.name}</p>
                  <p className="text-sm text-white/60">+91 {quickOnboardResult.phone}</p>
                </div>
              </div>
              
              {/* Activation Link */}
              <div className="p-3 bg-white/5 rounded-xl">
                <p className="text-xs text-white/40 mb-1">Signup Link</p>
                <div className="flex gap-2">
                  <Input
                    value={quickOnboardResult.activation_link}
                    readOnly
                    className="bg-white/5 border-white/10 text-white text-xs"
                  />
                  <Button
                    size="sm"
                    onClick={() => copyToClipboard(quickOnboardResult.activation_link)}
                    className="bg-purple-500 hover:bg-purple-600"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Copy Full Message Button */}
              <Button
                onClick={() => copyToClipboard(quickOnboardResult.copy_text || quickOnboardResult.whatsapp_message)}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Full Message
              </Button>
              
              {/* WhatsApp Button */}
              <a
                href={quickOnboardResult.whatsapp_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-4 bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold rounded-xl transition"
              >
                <MessageCircle className="w-5 h-5" />
                Send via WhatsApp
              </a>
              
              {/* Message Preview */}
              <div className="p-3 bg-white/5 rounded-xl max-h-48 overflow-y-auto">
                <p className="text-xs text-white/40 mb-2">Message Preview</p>
                <p className="text-sm text-white/80 whitespace-pre-line">{quickOnboardResult.whatsapp_message}</p>
              </div>
              
              {/* New Creator Button */}
              <Button
                variant="outline"
                onClick={() => setQuickOnboardResult(null)}
                className="w-full border-white/20 text-white hover:bg-white/10"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Another
              </Button>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center py-12">
              <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mb-4">
                <UserPlus className="w-10 h-10 text-white/20" />
              </div>
              <p className="text-white/40">Fill the form to generate signup & payment links</p>
              <p className="text-white/20 text-sm mt-2">Links will appear here</p>
            </div>
          )}
        </GlassCard>
      </div>

      {/* Instructions */}
      <GlassCard className="p-4">
        <h4 className="font-semibold text-white mb-3">📱 Quick Onboard Flow (90 seconds)</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-start gap-2">
            <span className="w-6 h-6 bg-[#FF0080] rounded-full flex items-center justify-center text-white text-xs font-bold">1</span>
            <div>
              <p className="text-white font-medium">Get Info</p>
              <p className="text-white/40">Ask name, phone, Instagram</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-6 h-6 bg-[#FF0080] rounded-full flex items-center justify-center text-white text-xs font-bold">2</span>
            <div>
              <p className="text-white font-medium">Fill Form</p>
              <p className="text-white/40">Enter details above</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-6 h-6 bg-[#FF0080] rounded-full flex items-center justify-center text-white text-xs font-bold">3</span>
            <div>
              <p className="text-white font-medium">Send WhatsApp</p>
              <p className="text-white/40">One-click share link</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-6 h-6 bg-[#25D366] rounded-full flex items-center justify-center text-white text-xs font-bold">4</span>
            <div>
              <p className="text-white font-medium">User Completes</p>
              <p className="text-white/40">Sets password & pays</p>
            </div>
          </div>
        </div>
      </GlassCard>
    </div>
  );

  const renderInstaContentGenerator = () => (
    <div className="space-y-6">
      <SectionHeader title="Instagram Content Generator" subtitle="Create promotional content for @foundstar_" icon={Instagram} />
      
      <GlassCard className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Panel - Controls */}
          <div className="space-y-6">
            {/* Content Type */}
            <div>
              <Label className="text-white/70 mb-2 block">Content Type</Label>
              <div className="flex gap-2">
                <Button
                  variant={instaContentType === "new-pro-creator" ? "default" : "outline"}
                  onClick={() => setInstaContentType("new-pro-creator")}
                  className={instaContentType === "new-pro-creator" ? "bg-gradient-to-r from-[#FF0080] to-[#B026FF]" : ""}
                >
                  <BadgeCheck className="w-4 h-4 mr-2" />
                  New Pro Creator
                </Button>
              </div>
            </div>

            {/* Source Selection */}
            <div className="p-4 bg-white/5 rounded-xl space-y-4">
              <div className="flex items-center gap-4">
                <Button
                  size="sm"
                  variant={selectedCreatorForInsta ? "default" : "outline"}
                  onClick={() => { setSelectedCreatorForInsta(null); setDemoCreatorPhoto(null); setDemoPortfolioImages([]); }}
                  className={!selectedCreatorForInsta ? "bg-purple-500/20 border-purple-500" : ""}
                >
                  Demo Creator
                </Button>
                <Button
                  size="sm"
                  variant={selectedCreatorForInsta ? "outline" : "default"}
                  onClick={() => setDemoCreatorName("")}
                  className={selectedCreatorForInsta ? "bg-purple-500/20 border-purple-500" : ""}
                >
                  Real Creator
                </Button>
              </div>

              {!selectedCreatorForInsta ? (
                /* Demo Creator Form */
                <div className="space-y-3">
                  {/* ONE-CLICK MAGIC BUTTON */}
                  <Button
                    onClick={generateMagicCreator}
                    className="w-full bg-gradient-to-r from-yellow-500 via-orange-500 to-pink-500 hover:from-yellow-600 hover:via-orange-600 hover:to-pink-600 text-white font-bold py-3 shadow-lg"
                    data-testid="magic-generate-btn"
                  >
                    <Zap className="w-5 h-5 mr-2" />
                    One-Click Magic Generate
                    <Sparkles className="w-4 h-4 ml-2" />
                  </Button>
                  <p className="text-white/40 text-xs text-center">Instantly generates fake Indian creator with photo & portfolio</p>
                  
                  <div className="border-t border-white/10 pt-3 mt-2">
                    <p className="text-white/50 text-xs mb-2">Or customize manually:</p>
                  </div>

                  <div>
                    <Label className="text-white/50 text-xs">Creator Name</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={demoCreatorName}
                        onChange={(e) => setDemoCreatorName(e.target.value)}
                        placeholder="Enter name..."
                        className="bg-white/5 border-white/10 text-white"
                      />
                      <Button size="sm" onClick={generateRandomName} className="bg-purple-500 hover:bg-purple-600 whitespace-nowrap">
                        <Sparkles className="w-4 h-4 mr-1" />
                        Random
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label className="text-white/50 text-xs">Category</Label>
                    <select
                      value={demoCreatorCategory}
                      onChange={(e) => setDemoCreatorCategory(e.target.value)}
                      className="w-full mt-1 p-2 bg-white/5 border border-white/10 rounded-lg text-white"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat} className="bg-[#1a1a2e]">{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label className="text-white/50 text-xs">Profile Photo</Label>
                    <div className="flex items-center gap-3 mt-1">
                      {demoCreatorPhoto && (
                        <div className="relative">
                          <img src={demoCreatorPhoto} alt="Profile" className="w-12 h-12 rounded-full object-cover border-2 border-purple-500" />
                          <button 
                            onClick={() => setDemoCreatorPhoto(null)} 
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center"
                          >
                            ×
                          </button>
                        </div>
                      )}
                      <Input
                        type="file"
                        accept="image/*"
                        className="bg-white/5 border-white/10 text-white text-xs flex-1"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => setDemoCreatorPhoto(reader.result);
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-white/50 text-xs">Portfolio Images (up to 4)</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      multiple
                      className="bg-white/5 border-white/10 text-white text-xs mt-1"
                      onChange={handlePortfolioUpload}
                    />
                    {demoPortfolioImages.length > 0 && (
                      <div className="flex gap-2 mt-2">
                        {demoPortfolioImages.map((img, i) => (
                          <img key={i} src={img} alt="" className="w-12 h-12 rounded object-cover" />
                        ))}
                        <button onClick={() => setDemoPortfolioImages([])} className="text-red-400 text-xs">Clear</button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Real Creator Selector */
                <div>
                  <Label className="text-white/50 text-xs">Select Pro Creator</Label>
                  <Select
                    value={selectedCreatorForInsta?.user_id || ""}
                    onValueChange={(val) => {
                      const creator = talents.find(t => t.user_id === val && t.is_verified);
                      setSelectedCreatorForInsta(creator);
                    }}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                      <SelectValue placeholder="Choose a Pro Creator..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-white/10 max-h-[250px]">
                      {talents.filter(t => t.is_verified).map((t) => (
                        <SelectItem key={t.user_id} value={t.user_id} className="text-white hover:bg-white/10">
                          <div className="flex items-center gap-2">
                            <Avatar className="w-6 h-6">
                              <AvatarImage src={getImageUrl(t.picture || t.photo_url)} />
                              <AvatarFallback className="bg-pink-500/20 text-pink-300 text-xs">{t.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            {t.name}
                            <BadgeCheck className="w-4 h-4 text-purple-400" />
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <Button
              onClick={downloadInstaCard}
              disabled={generatingInstaCard || (!demoCreatorName && !selectedCreatorForInsta)}
              className="w-full bg-gradient-to-r from-[#FF0080] to-[#B026FF] hover:opacity-90"
            >
              {generatingInstaCard ? (
                <><span className="animate-spin mr-2">⏳</span> Generating...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Download for Instagram</>
              )}
            </Button>
          </div>

          {/* Right Panel - Preview */}
          <div className="flex items-center justify-center">
            <div
              ref={instaCardRef}
              className="relative w-full max-w-[380px] rounded-3xl overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #1a0b2e 0%, #2d1b4e 50%, #1a0b2e 100%)",
                aspectRatio: "1/1"
              }}
            >
              {/* Decorative elements */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {[...Array(15)].map((_, i) => (
                  <Sparkles
                    key={i}
                    className="absolute text-yellow-300/20"
                    style={{
                      top: `${Math.random() * 100}%`,
                      left: `${Math.random() * 100}%`,
                      width: 8 + Math.random() * 10,
                      height: 8 + Math.random() * 10,
                    }}
                  />
                ))}
              </div>

              <div className="relative z-10 p-6 h-full flex flex-col">
                {/* Header */}
                <div className="text-center mb-4">
                  <div
                    className="inline-flex items-center gap-2 text-white px-4 py-2 rounded-full text-sm font-bold"
                    style={{ background: "linear-gradient(135deg, #7B2FBE 0%, #FF0080 100%)" }}
                  >
                    <BadgeCheck className="w-4 h-4" />
                    NEW PRO CREATOR
                  </div>
                </div>

                {/* Creator Photo */}
                <div className="flex-1 flex flex-col items-center justify-center">
                  <div className="relative mb-4">
                    <div className="w-28 h-28 rounded-full p-1" style={{ background: "linear-gradient(135deg, #FF0080 0%, #7B2FBE 50%, #00D9FF 100%)" }}>
                      <div className="w-full h-full rounded-full overflow-hidden flex items-center justify-center relative" style={{ background: "linear-gradient(135deg, #DB2777 0%, #9333EA 100%)" }}>
                        <span className="text-white text-4xl font-bold absolute inset-0 flex items-center justify-center z-0">
                          {getCreatorName().charAt(0).toUpperCase()}
                        </span>
                        {getCreatorPhoto() && (
                          <img src={getCreatorPhoto()} alt="" className="w-full h-full object-cover absolute inset-0 z-10" />
                        )}
                      </div>
                    </div>
                    {/* Verified badge */}
                    <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-[#7B2FBE] rounded-full flex items-center justify-center border-4 border-[#1a0b2e]">
                      <BadgeCheck className="w-5 h-5 text-white" />
                    </div>
                  </div>

                  <h2 className="text-white font-bold text-2xl text-center mb-1">{getCreatorName()}</h2>
                  <p className="text-white/60 text-sm mb-4">{getCreatorCategory()}</p>

                  {/* Portfolio Preview */}
                  {demoPortfolioImages.length > 0 && (
                    <div className="flex gap-2 mb-4">
                      {demoPortfolioImages.slice(0, 4).map((img, i) => (
                        <div key={i} className="w-16 h-16 rounded-lg overflow-hidden border-2 border-white/20">
                          <img src={img} alt="" className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}

                  <p className="text-white/50 text-sm text-center">Just joined FOUNDSTAR as a</p>
                  <p className="text-[#FF69B4] font-bold text-lg">Pro Creator ✨</p>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-center gap-2 pt-4 border-t border-white/10">
                  <Star className="w-4 h-4 text-[#FF69B4]" />
                  <span className="text-white/60 text-xs font-medium">FOUNDSTAR</span>
                  <span className="text-white/40 text-xs">• @foundstar_</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </GlassCard>
    </div>
  );

  // --- Stars of the Day ---
  const renderStarsOfDay = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Stars of the Day" subtitle="Curate up to 5 featured stars" icon={Star}
        right={
          <div className="flex gap-2">
            {selectedStarIds.length > 0 && <Button variant="outline" onClick={handleClearStars} disabled={processing} className="bg-white/5 border-white/10 text-white hover:bg-white/10">Clear</Button>}
            <Button onClick={handleSetStars} disabled={processing || selectedStarIds.length === 0} className="bg-gradient-to-r from-[#FF0080] to-[#B026FF] text-white">
              <Star className="w-4 h-4 mr-2" />{processing ? "Saving..." : `Set Stars (${selectedStarIds.length}/5)`}
            </Button>
          </div>
        }
      />

      {starSettings.selected_stars?.length > 0 && (
        <GlassCard className="p-5 border-[#FF0080]/20">
          <h4 className="font-manrope font-bold text-white mb-3 flex items-center gap-2"><Star className="w-4 h-4 text-[#FF0080] fill-[#FF0080]" />Currently Featured</h4>
          <div className="flex flex-wrap gap-2">
            {starSettings.selected_stars.map((s) => (
              <div key={s.user_id} className="flex items-center gap-2 bg-white/5 border border-white/10 px-3 py-2 rounded-full">
                <Avatar className="w-7 h-7"><AvatarImage src={s.picture} /><AvatarFallback className="bg-gradient-to-br from-[#FF0080] to-[#B026FF] text-white text-xs">{s.name?.charAt(0)}</AvatarFallback></Avatar>
                <span className="text-sm font-manrope text-white">{s.name}</span>
                <Star className="w-4 h-4 text-[#FF0080] fill-[#FF0080]" />
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      <div>
        <h4 className="font-manrope font-bold text-white mb-3">Select Influencers</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {influencers.filter(i => i.is_verified).map((inf) => {
            const selected = selectedStarIds.includes(inf.user_id);
            return (
              <div key={inf.user_id} onClick={() => toggleStarSelection(inf.user_id)} className={`p-4 rounded-xl border-2 cursor-pointer transition ${selected ? "border-[#FF0080] bg-[#FF0080]/5 shadow-[0_0_20px_rgba(255,0,128,0.2)]" : "border-white/10 bg-white/[0.02] hover:border-white/20"}`}>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Avatar className="w-11 h-11"><AvatarImage src={inf.picture} /><AvatarFallback className="bg-gradient-to-br from-[#FF0080] to-[#B026FF] text-white">{inf.name?.charAt(0)}</AvatarFallback></Avatar>
                    {selected && <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF0080] rounded-full flex items-center justify-center shadow-[0_0_10px_#FF0080]"><Star className="w-3 h-3 text-white fill-white" /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-manrope font-semibold text-white truncate">{inf.name}</p>
                    <p className="text-xs text-white/50 font-mono">{fmt(inf.followers_count)} followers</p>
                    <p className="text-xs text-white/30">{inf.location}</p>
                  </div>
                  {inf.is_verified && <BadgeCheck className="w-5 h-5 text-sky-400 shrink-0" />}
                </div>
              </div>
            );
          })}
        </div>
        {influencers.filter(i => i.is_verified).length === 0 && (
          <GlassCard className="p-12 text-center"><Users className="w-10 h-10 text-white/20 mx-auto mb-3" /><p className="text-white/50">No verified influencers yet</p></GlassCard>
        )}
      </div>
    </div>
  );

  // --- Landing CMS ---
  const renderLanding = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Landing CMS" subtitle="Featured content on the public landing page" icon={LayoutDashboard}
        right={<Button onClick={saveLandingContent} disabled={savingLanding} className="bg-gradient-to-r from-[#FF0080] to-[#B026FF] text-white">{savingLanding ? "Saving..." : "Save Changes"}</Button>}
      />

      {/* Featured Influencers */}
      <GlassCard className="p-5">
        <h3 className="font-manrope font-bold text-white mb-2 flex items-center gap-2"><Star className="w-5 h-5 text-[#FF0080]" />Featured Influencers <span className="text-sm font-mono text-white/40">({(landingContent.featured_influencers || []).length}/6)</span></h3>
        <p className="text-sm text-white/50 mb-4">Up to 6 influencers shown on the Brand landing page</p>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {influencers.filter(i => i.is_verified).map((inf) => {
            const selected = (landingContent.featured_influencers || []).includes(inf.user_id);
            return (
              <div key={inf.user_id} onClick={() => toggleFeaturedInfluencer(inf.user_id)} className={`p-3 rounded-xl border-2 cursor-pointer transition ${selected ? "border-[#FF0080] bg-[#FF0080]/5" : "border-white/10 bg-white/[0.02] hover:border-white/20"}`}>
                <div className="w-14 h-14 mx-auto mb-2 rounded-full overflow-hidden bg-white/5">
                  {inf.picture ? <img src={inf.picture.startsWith("http") ? inf.picture : `${API}/files/${inf.picture}`} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-xl font-bold text-white/40">{inf.name?.charAt(0)}</div>}
                </div>
                <p className="text-xs font-manrope text-center text-white truncate">{inf.name}</p>
                <p className="text-[10px] font-mono text-white/40 text-center">{fmt(inf.followers_count)}</p>
                {selected && <p className="mt-1 text-[9px] font-mono text-center text-[#FF0080] uppercase tracking-widest">· Featured ·</p>}
              </div>
            );
          })}
        </div>
      </GlassCard>

      {/* Featured Brands */}
      <GlassCard className="p-5">
        <h3 className="font-manrope font-bold text-white mb-2 flex items-center gap-2"><Building2 className="w-5 h-5 text-[#B026FF]" />Featured Brands <span className="text-sm font-mono text-white/40">({(landingContent.featured_brands || []).length}/6)</span></h3>
        <p className="text-sm text-white/50 mb-4">Up to 6 brands shown on the Influencer landing page</p>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {brands.filter(b => b.is_approved !== false).map((b) => {
            const selected = (landingContent.featured_brands || []).includes(b.user_id);
            return (
              <div key={b.user_id} onClick={() => toggleFeaturedBrand(b.user_id)} className={`p-3 rounded-xl border-2 cursor-pointer transition ${selected ? "border-[#B026FF] bg-[#B026FF]/5" : "border-white/10 bg-white/[0.02] hover:border-white/20"}`}>
                <div className="w-14 h-14 mx-auto mb-2 rounded-full overflow-hidden bg-white/5 flex items-center justify-center">
                  <span className="text-xl font-bold text-white/60">{b.name?.charAt(0)}</span>
                </div>
                <p className="text-xs font-manrope text-center text-white truncate">{b.name}</p>
                {selected && <p className="mt-1 text-[9px] font-mono text-center text-[#B026FF] uppercase tracking-widest">· Featured ·</p>}
              </div>
            );
          })}
        </div>
      </GlassCard>

      {/* Brand Testimonial Videos */}
      <GlassCard className="p-5">
        <h3 className="font-manrope font-bold text-white mb-2 flex items-center gap-2"><Youtube className="w-5 h-5 text-red-400" />Brand Testimonial Videos</h3>
        <p className="text-sm text-white/50 mb-4">Language-specific video testimonials shown on Brand landing page</p>

        <div className="bg-black/20 border border-white/5 p-4 rounded-xl mb-4">
          <h4 className="font-manrope font-semibold text-white mb-3">Add New Video</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label className="text-xs text-white/60">Language</Label>
              <select value={newVideo.language} onChange={(e) => setNewVideo({ ...newVideo, language: e.target.value })} className="w-full p-2 bg-black/30 border border-white/10 rounded-lg mt-1 text-white">
                <option value="English">English</option><option value="Hindi">Hindi</option><option value="Marathi">Marathi</option>
              </select>
            </div>
            <div><Label className="text-xs text-white/60">Video URL / Upload</Label>
              <div className="flex gap-2 mt-1">
                <Input placeholder="https://youtube.com/..." value={newVideo.video_url} onChange={(e) => { const thumb = getYouTubeThumbnail(e.target.value); setNewVideo({ ...newVideo, video_url: e.target.value, thumbnail_url: thumb || newVideo.thumbnail_url }); }} className="flex-1 bg-black/30 border-white/10 text-white" />
                <label className="bg-gradient-to-r from-[#FF0080] to-[#B026FF] text-white px-3 py-2 rounded-lg cursor-pointer text-sm flex items-center whitespace-nowrap">
                  {uploadingVideo ? "..." : "Upload"}
                  <input type="file" accept="video/*" className="hidden" onChange={async (e) => { const r = await handleVideoUpload(e.target.files[0]); if (r) setNewVideo({ ...newVideo, video_url: r.videoUrl, thumbnail_url: r.thumbnailUrl || newVideo.thumbnail_url }); }} />
                </label>
              </div>
            </div>
            <div><Label className="text-xs text-white/60">Thumbnail URL</Label><Input value={newVideo.thumbnail_url} onChange={(e) => setNewVideo({ ...newVideo, thumbnail_url: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
            <div><Label className="text-xs text-white/60">Brand Owner</Label><Input placeholder="Rajesh Kumar" value={newVideo.name} onChange={(e) => setNewVideo({ ...newVideo, name: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
            <div><Label className="text-xs text-white/60">Company</Label><Input placeholder="TechGadgets India" value={newVideo.company} onChange={(e) => setNewVideo({ ...newVideo, company: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
            <div><Label className="text-xs text-white/60">Quote</Label><Input placeholder="Since joining..." value={newVideo.quote} onChange={(e) => setNewVideo({ ...newVideo, quote: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
          </div>
          <Button onClick={addTestimonialVideo} className="mt-3 bg-gradient-to-r from-[#FF0080] to-[#B026FF] text-white"><Plus className="w-4 h-4 mr-1" />Add Video</Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(landingContent.testimonial_videos || []).map((v) => (
            <div key={v.id} className="rounded-xl overflow-hidden bg-white/[0.02] border border-white/10">
              <div className="relative h-32 bg-black/30">
                {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Youtube className="w-10 h-10 text-red-400" /></div>}
                <span className="absolute top-2 right-2 bg-black/60 text-white px-2 py-0.5 rounded-full text-[10px] font-mono">{v.language}</span>
              </div>
              <div className="p-3">
                <p className="font-manrope font-semibold text-white text-sm">{v.name}</p>
                <p className="text-xs text-white/40">{v.company}</p>
                <p className="text-xs text-white/50 mt-1 line-clamp-2 italic">"{v.quote}"</p>
                <div className="flex items-center justify-between mt-2">
                  <a href={v.video_url} target="_blank" rel="noreferrer" className="text-xs text-sky-400 hover:underline">View →</a>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-400 hover:bg-rose-500/10" onClick={() => removeTestimonialVideo(v.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
          {(landingContent.testimonial_videos || []).length === 0 && <div className="col-span-3 text-center py-8 text-white/30"><Youtube className="w-10 h-10 mx-auto mb-2 opacity-40" /><p className="text-sm">No videos yet</p></div>}
        </div>
      </GlassCard>

      {/* Influencer Testimonial Videos */}
      <GlassCard className="p-5">
        <h3 className="font-manrope font-bold text-white mb-2 flex items-center gap-2"><Star className="w-5 h-5 text-[#FF0080]" />Influencer Testimonial Videos</h3>
        <p className="text-sm text-white/50 mb-4">Video testimonials shown on the Influencer landing page</p>

        <div className="bg-black/20 border border-white/5 p-4 rounded-xl mb-4">
          <h4 className="font-manrope font-semibold text-white mb-3">Add Influencer Video</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label className="text-xs text-white/60">Language</Label>
              <select value={newInfluencerVideo.language} onChange={(e) => setNewInfluencerVideo({ ...newInfluencerVideo, language: e.target.value })} className="w-full p-2 bg-black/30 border border-white/10 rounded-lg mt-1 text-white">
                <option value="English">English</option><option value="Hindi">Hindi</option><option value="Marathi">Marathi</option>
              </select>
            </div>
            <div><Label className="text-xs text-white/60">Video URL / Upload</Label>
              <div className="flex gap-2 mt-1">
                <Input placeholder="https://youtube.com/..." value={newInfluencerVideo.video_url} onChange={(e) => { const thumb = getYouTubeThumbnail(e.target.value); setNewInfluencerVideo({ ...newInfluencerVideo, video_url: e.target.value, thumbnail_url: thumb || newInfluencerVideo.thumbnail_url }); }} className="flex-1 bg-black/30 border-white/10 text-white" />
                <label className="bg-gradient-to-r from-[#FF0080] to-[#B026FF] text-white px-3 py-2 rounded-lg cursor-pointer text-sm flex items-center whitespace-nowrap">
                  {uploadingVideo ? "..." : "Upload"}
                  <input type="file" accept="video/*" className="hidden" onChange={async (e) => { const r = await handleVideoUpload(e.target.files[0]); if (r) setNewInfluencerVideo({ ...newInfluencerVideo, video_url: r.videoUrl, thumbnail_url: r.thumbnailUrl || newInfluencerVideo.thumbnail_url }); }} />
                </label>
              </div>
            </div>
            <div><Label className="text-xs text-white/60">Thumbnail URL</Label><Input value={newInfluencerVideo.thumbnail_url} onChange={(e) => setNewInfluencerVideo({ ...newInfluencerVideo, thumbnail_url: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
            <div><Label className="text-xs text-white/60">Influencer Name</Label><Input placeholder="Priya Sharma" value={newInfluencerVideo.name} onChange={(e) => setNewInfluencerVideo({ ...newInfluencerVideo, name: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
            <div><Label className="text-xs text-white/60">Followers</Label><Input placeholder="125K" value={newInfluencerVideo.followers} onChange={(e) => setNewInfluencerVideo({ ...newInfluencerVideo, followers: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
            <div><Label className="text-xs text-white/60">Quote</Label><Input placeholder="FoundStar changed..." value={newInfluencerVideo.quote} onChange={(e) => setNewInfluencerVideo({ ...newInfluencerVideo, quote: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
          </div>
          <Button onClick={addInfluencerTestimonialVideo} disabled={uploadingVideo} className="mt-3 bg-gradient-to-r from-[#FF0080] to-[#B026FF] text-white"><Plus className="w-4 h-4 mr-1" />Add Video</Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(landingContent.influencer_testimonial_videos || []).map((v) => (
            <div key={v.id} className="rounded-xl overflow-hidden bg-white/[0.02] border border-[#FF0080]/20">
              <div className="relative h-32 bg-black/30">
                {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Star className="w-10 h-10 text-[#FF0080]" /></div>}
                <span className="absolute top-2 right-2 bg-black/60 text-white px-2 py-0.5 rounded-full text-[10px] font-mono">{v.language}</span>
              </div>
              <div className="p-3">
                <p className="font-manrope font-semibold text-[#FF4DA6] text-sm">{v.name}</p>
                <p className="text-xs text-white/40 font-mono">{v.followers}</p>
                <p className="text-xs text-white/50 mt-1 line-clamp-2 italic">"{v.quote}"</p>
                <div className="flex items-center justify-between mt-2">
                  {v.video_url?.startsWith("/api") ? <a href={`${API.replace("/api", "")}${v.video_url}`} target="_blank" rel="noreferrer" className="text-xs text-[#FF4DA6] hover:underline">View →</a> : <a href={v.video_url} target="_blank" rel="noreferrer" className="text-xs text-sky-400 hover:underline">View →</a>}
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-400 hover:bg-rose-500/10" onClick={() => removeInfluencerTestimonialVideo(v.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
          {(landingContent.influencer_testimonial_videos || []).length === 0 && <div className="col-span-3 text-center py-8 text-white/30"><Star className="w-10 h-10 mx-auto mb-2 opacity-40" /><p className="text-sm">No videos yet</p></div>}
        </div>
      </GlassCard>
    </div>
  );

  // ==================== Brand Pro Syncs Section ====================
  const renderBrandProSyncs = () => (
    <div className="space-y-6">
      <SectionHeader title="Brand Pro Syncs" subtitle="External app integrations for Brand Pro subscriptions" icon={Zap} />
      
      <GlassCard className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">Recent Syncs</h3>
          <Button size="sm" onClick={fetchBrandProSyncs} className="bg-purple-500 hover:bg-purple-600">
            Refresh
          </Button>
        </div>
        
        {brandProSyncs.length === 0 ? (
          <div className="text-center py-12 text-white/40">
            <Zap className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>No Brand Pro syncs yet</p>
            <p className="text-sm mt-2">External apps will sync here via API</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-white/40 text-left border-b border-white/10">
                  <th className="p-3">Brand</th>
                  <th className="p-3">Email</th>
                  <th className="p-3">Type</th>
                  <th className="p-3">Amount</th>
                  <th className="p-3">Source</th>
                  <th className="p-3">Synced At</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {brandProSyncs.map((sync, idx) => (
                  <tr key={sync.sync_id || idx} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3 text-white font-medium">{sync.brand_name || '-'}</td>
                    <td className="p-3 text-white/70">{sync.brand_email}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        sync.subscription_type === 'yearly' ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'
                      }`}>
                        {sync.subscription_type}
                      </span>
                    </td>
                    <td className="p-3 text-emerald-400">₹{sync.amount_paid || '-'}</td>
                    <td className="p-3 text-white/50">{sync.source}</td>
                    <td className="p-3 text-white/50">
                      {sync.synced_at ? new Date(sync.synced_at).toLocaleDateString('en-IN', { 
                        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
                      }) : '-'}
                    </td>
                    <td className="p-3">
                      <span className="px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-xs">
                        {sync.status || 'completed'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      {/* API Info Card */}
      <GlassCard className="p-4">
        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4 text-purple-400" />
          API Integration Info
        </h4>
        <div className="bg-white/5 rounded-xl p-4 font-mono text-sm">
          <p className="text-white/60 mb-2">Endpoint:</p>
          <code className="text-emerald-400">POST /api/integrations/app/brand-pro</code>
          
          <p className="text-white/60 mt-4 mb-2">Header:</p>
          <code className="text-yellow-400">X-App-Sync-Key: [your-key]</code>
          
          <p className="text-white/60 mt-4 mb-2">Body:</p>
          <pre className="text-purple-300 text-xs overflow-x-auto">{`{
  "brand_email": "brand@example.com",
  "brand_name": "Brand Name",
  "subscription_type": "yearly",
  "amount_paid": 999,
  "source": "external_app"
}`}</pre>
        </div>
      </GlassCard>
    </div>
  );

  // ==================== Sidebar Navigation Config ====================
  const navItems = [
    { id: "overview", label: "Overview", icon: LayoutDashboard, badge: null },
    { id: "quick-onboard", label: "Quick Onboard", icon: UserPlus, badge: null, badgeTone: "emerald" },
    { id: "signups", label: "Signups", icon: User, badge: signupsStats?.incomplete_signups || (signups.filter(u => !u.profile_completed).length || null), badgeTone: "orange" },
    { id: "verifications", label: "Verifications", icon: BadgeCheck, badge: pendingRequests.length || null },
    { id: "talents", label: "Creators", icon: Users, badge: talents.length || null, badgeTone: "muted" },
    { id: "brands", label: "Brands", icon: Briefcase, badge: null },
    { id: "pending-brands", label: "Pending Brands", icon: Clock, badge: pendingBrands.length || null, badgeTone: "orange" },
    { id: "deals", label: "Deals", icon: Handshake, badge: disputedDeals.length || null, badgeTone: "orange" },
    { id: "opportunities", label: "Opportunities", icon: Zap, badge: pendingApplications.length || null, badgeTone: "orange" },
    { id: "withdrawals", label: "Withdrawals", icon: Wallet, badge: pendingWithdrawals.length || null, badgeTone: "emerald" },
    { id: "notifications", label: "Send Push", icon: Bell, badge: null },
    { id: "promo", label: "Promo Codes", icon: Tag, badge: null },
    { id: "collab-card", label: "Collab Card", icon: Image, badge: null },
    { id: "insta-content", label: "Insta Content", icon: Instagram, badge: null },
    { id: "stars", label: "Stars of the Day", icon: Star, badge: null },
    { id: "landing", label: "Landing CMS", icon: LayoutDashboard, badge: null },
    { id: "brand-pro-syncs", label: "Brand Pro Syncs", icon: Zap, badge: brandProSyncs.length || null, badgeTone: "purple" },
  ];

  const sectionRenderers = {
    overview: renderOverview,
    "quick-onboard": renderQuickOnboard,
    signups: renderSignups,
    verifications: renderVerifications,
    talents: renderTalents,
    brands: renderBrands,
    "pending-brands": renderPendingBrands,
    deals: renderDeals,
    opportunities: renderOpportunities,
    withdrawals: renderWithdrawals,
    notifications: renderNotifications,
    promo: renderPromoCodes,
    "collab-card": renderCollabCardGenerator,
    "insta-content": renderInstaContentGenerator,
    stars: renderStarsOfDay,
    landing: renderLanding,
    "brand-pro-syncs": renderBrandProSyncs,
  };

  const Sidebar = () => (
    <aside className={`${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 left-0 z-50 h-screen w-72 bg-[#0B0A11] border-r border-white/5 flex flex-col transition-transform duration-300`}>
      {/* Logo */}
      <div className="p-5 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF0080] to-[#B026FF] flex items-center justify-center shadow-[0_0_30px_rgba(255,0,128,0.4)]">
            <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <p className="font-manrope font-extrabold text-lg leading-none">
              <span className="bg-gradient-to-r from-[#FF0080] to-[#B026FF] bg-clip-text text-transparent">Found</span>
              <span className="text-white">Star</span>
            </p>
            <p className="font-mono text-[9px] text-white/40 tracking-[0.3em] uppercase mt-1">Admin Panel</p>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="ml-auto lg:hidden text-white/60 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = activeSection === item.id;
          const badgeColor = item.badgeTone === "emerald" ? "bg-emerald-500 text-white"
            : item.badgeTone === "orange" ? "bg-orange-500 text-white"
            : item.badgeTone === "muted" ? "bg-white/10 text-white/70"
            : "bg-[#FF0080] text-white";
          return (
            <button
              key={item.id}
              onClick={() => { setActiveSection(item.id); setSidebarOpen(false); }}
              data-testid={`sidebar-${item.id}`}
              className={`w-full group relative flex items-center gap-3 px-3 py-2.5 rounded-xl font-manrope text-sm transition ${active
                ? "bg-gradient-to-r from-[#FF0080]/15 to-[#B026FF]/15 text-white border border-[#FF0080]/30 shadow-[0_0_20px_rgba(255,0,128,0.15)]"
                : "text-white/60 hover:text-white hover:bg-white/[0.04] border border-transparent"}`}
            >
              {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-gradient-to-b from-[#FF0080] to-[#B026FF]" />}
              <Icon className={`w-4 h-4 ${active ? "text-[#FF4DA6]" : "text-white/50 group-hover:text-white/80"}`} />
              <span className="flex-1 text-left">{item.label}</span>
              {item.badge != null && item.badge > 0 && (
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${badgeColor}`}>{item.badge}</span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-white/5">
        <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF0080] to-[#B026FF] flex items-center justify-center">
            <span className="font-manrope font-bold text-white text-sm">{adminUser?.name?.charAt(0) || "A"}</span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-manrope text-white truncate">{adminUser?.name || "Admin"}</p>
            <p className="text-[10px] text-white/40 font-mono uppercase tracking-widest">Root · Admin</p>
          </div>
          <button onClick={handleLogout} data-testid="admin-logout-btn" className="text-white/50 hover:text-[#FF0080] transition">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );

  // ==================== Render ====================
  if (loading) {
    return (
      <div className="min-h-screen bg-[#08070D] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative w-12 h-12">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#FF0080] to-[#B026FF] blur-xl opacity-60 animate-pulse" />
            <div className="relative w-12 h-12 border-4 border-[#FF0080]/30 border-t-[#FF0080] rounded-full animate-spin" />
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Loading Command Center</p>
        </div>
      </div>
    );
  }

  const activeNav = navItems.find(n => n.id === activeSection) || navItems[0];

  return (
    <div className="min-h-screen bg-[#08070D] text-white font-manrope relative overflow-hidden">
      {/* Ambient glow */}
      <div className="pointer-events-none fixed -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#FF0080]/10 blur-[160px]" />
      <div className="pointer-events-none fixed -bottom-40 -right-40 w-[700px] h-[700px] rounded-full bg-[#B026FF]/10 blur-[180px]" />

      {/* Mobile backdrop */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <div className="relative z-10 flex">
        <Sidebar />

        <main className="flex-1 min-w-0">
          {/* Top bar */}
          <div className="sticky top-0 z-30 bg-[#08070D]/80 backdrop-blur-xl border-b border-white/5">
            <div className="px-4 md:px-8 py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button onClick={() => setSidebarOpen(true)} className="lg:hidden w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white">
                  <Menu className="w-5 h-5" />
                </button>
                <div className="hidden md:block">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Section</p>
                  <p className="font-manrope font-bold text-white">{activeNav.label}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-white/60">Online</span>
                </div>
                <Button onClick={handleLogout} variant="ghost" className="text-white/60 hover:text-white hover:bg-white/10" data-testid="admin-logout-btn-top">
                  <LogOut className="w-4 h-4 md:mr-2" /><span className="hidden md:inline">Logout</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Section content */}
          <div className="p-4 md:p-8 max-w-[1600px] mx-auto">
            {sectionRenderers[activeSection]?.()}
          </div>
        </main>
      </div>

      {/* ==================== Dialogs ==================== */}

      {/* Reject Dialog */}
      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="bg-[#0B0A11] border border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-manrope">Reject Verification Request</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-white/60">Reject <strong className="text-white">{selectedRequest?.name}</strong>'s verification request?</p>
            <div>
              <Label className="text-xs text-white/60">Reason (optional)</Label>
              <Textarea placeholder="Enter rejection reason..." value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="bg-black/30 border-white/10 text-white rounded-xl mt-1" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSelectedRequest(null)} className="flex-1 bg-white/5 border-white/10 text-white hover:bg-white/10">Cancel</Button>
              <Button onClick={() => handleReject(selectedRequest?.request_id)} disabled={processing} className="flex-1 bg-rose-500/80 hover:bg-rose-500 text-white">{processing ? "Rejecting..." : "Reject"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Full Profile Dialog */}
      <Dialog open={!!viewProfileRequest} onOpenChange={() => setViewProfileRequest(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0B0A11] border border-white/10 text-white">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-manrope"><User className="w-5 h-5 text-[#FF0080]" />Profile Details</DialogTitle></DialogHeader>
          {viewProfileRequest && (
            <div className="space-y-5 mt-4">
              <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-[#FF0080]/10 to-[#B026FF]/10 rounded-xl border border-white/10">
                <Avatar className="w-16 h-16 border-2 border-white/20"><AvatarImage src={viewProfileRequest.picture?.startsWith("http") ? viewProfileRequest.picture : viewProfileRequest.picture ? `${API}/files/${viewProfileRequest.picture}` : null} /><AvatarFallback className="bg-gradient-to-br from-[#FF0080] to-[#B026FF] text-white font-bold text-2xl">{viewProfileRequest.name?.charAt(0) || "U"}</AvatarFallback></Avatar>
                <div>
                  <h3 className="font-manrope font-bold text-xl text-white">{viewProfileRequest.name}</h3>
                  <p className="text-white/50 text-sm">{viewProfileRequest.email}</p>
                  <div className="mt-1"><StatusBadge status={viewProfileRequest.status} /></div>
                </div>
              </div>
              <div className={`p-3 rounded-xl border ${viewProfileRequest.payment_id ? "bg-emerald-500/10 border-emerald-400/30" : "bg-amber-500/10 border-amber-400/30"}`}>
                <div className="flex items-center gap-3">
                  {viewProfileRequest.payment_id ? <CheckCircle className="w-6 h-6 text-emerald-400" /> : <XCircle className="w-6 h-6 text-amber-400" />}
                  <div>
                    <p className={`font-manrope font-semibold text-sm ${viewProfileRequest.payment_id ? "text-emerald-300" : "text-amber-300"}`}>{viewProfileRequest.payment_id ? "₹199 Payment Verified" : "Payment Not Verified"}</p>
                    {viewProfileRequest.payment_id && <p className="text-xs text-emerald-400/70 font-mono">ID: {viewProfileRequest.payment_id}</p>}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 rounded-lg bg-white/5"><p className="text-xs text-white/40 mb-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" />Location</p><p className="font-manrope text-white">{viewProfileRequest.location || "—"}</p></div>
                <div className="p-3 rounded-lg bg-white/5"><p className="text-xs text-white/40 mb-0.5 flex items-center gap-1"><Users className="w-3 h-3" />Followers</p><p className="font-mono text-white">{fmt(viewProfileRequest.followers_count)}</p></div>
                <div className="p-3 rounded-lg bg-white/5"><p className="text-xs text-white/40 mb-0.5 flex items-center gap-1"><Star className="w-3 h-3" />Platform</p><p className="font-manrope text-white">{viewProfileRequest.platform || "—"}</p></div>
                <div className="p-3 rounded-lg bg-white/5"><p className="text-xs text-white/40 mb-0.5 flex items-center gap-1"><Clock className="w-3 h-3" />Applied</p><p className="font-manrope text-white">{new Date(viewProfileRequest.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p></div>
              </div>
              {viewProfileRequest.niches?.length > 0 && (
                <div className="p-3 rounded-lg bg-white/5">
                  <p className="text-xs text-white/40 mb-2">Niches</p>
                  <div className="flex flex-wrap gap-1.5">{viewProfileRequest.niches.map((n, i) => (<span key={i} className="text-[10px] font-mono uppercase px-2 py-1 rounded-full bg-gradient-to-r from-[#FF0080] to-[#B026FF] text-white">{n}</span>))}</div>
                </div>
              )}
              {viewProfileRequest.bio && <div className="p-3 rounded-lg bg-white/5"><p className="text-xs text-white/40 mb-1 flex items-center gap-1"><FileText className="w-3 h-3" />Bio</p><p className="text-sm text-white/80">{viewProfileRequest.bio}</p></div>}

              <div className="space-y-2">
                {viewProfileRequest.instagram_link && <a href={viewProfileRequest.instagram_link.startsWith("http") ? viewProfileRequest.instagram_link : `https://${viewProfileRequest.instagram_link}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-pink-500/10 border border-white/10 hover:border-pink-400/30"><div className="w-9 h-9 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center"><Instagram className="w-4 h-4 text-white" /></div><div className="flex-1 min-w-0"><p className="font-manrope text-sm text-white">Instagram</p><p className="text-xs text-white/40 truncate">{viewProfileRequest.instagram_link}</p></div><ExternalLink className="w-4 h-4 text-white/40" /></a>}
                {viewProfileRequest.youtube_link && <a href={viewProfileRequest.youtube_link.startsWith("http") ? viewProfileRequest.youtube_link : `https://${viewProfileRequest.youtube_link}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-red-500/10 border border-white/10 hover:border-red-400/30"><div className="w-9 h-9 bg-red-500 rounded-full flex items-center justify-center"><Youtube className="w-4 h-4 text-white" /></div><div className="flex-1 min-w-0"><p className="font-manrope text-sm text-white">YouTube</p><p className="text-xs text-white/40 truncate">{viewProfileRequest.youtube_link}</p></div><ExternalLink className="w-4 h-4 text-white/40" /></a>}
                {viewProfileRequest.platform_link && <a href={viewProfileRequest.platform_link.startsWith("http") ? viewProfileRequest.platform_link : `https://${viewProfileRequest.platform_link}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 border border-white/10"><div className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center"><LinkIcon className="w-4 h-4 text-white" /></div><div className="flex-1 min-w-0"><p className="font-manrope text-sm text-white">Profile Link</p><p className="text-xs text-white/40 truncate">{viewProfileRequest.platform_link}</p></div><ExternalLink className="w-4 h-4 text-white/40" /></a>}
              </div>

              <div className="flex gap-3 pt-4 border-t border-white/10">
                <Button variant="outline" onClick={() => { setViewProfileRequest(null); setSelectedRequest(viewProfileRequest); }} disabled={processing} className="flex-1 bg-rose-500/10 border-rose-400/30 text-rose-300 hover:bg-rose-500/20">
                  <XCircle className="w-4 h-4 mr-2" />{viewProfileRequest.status === "rejected" ? "Already Rejected" : "Reject"}
                </Button>
                <Button onClick={() => { handleApprove(viewProfileRequest.request_id); setViewProfileRequest(null); }} disabled={processing} className="flex-1 bg-gradient-to-r from-[#FF0080] to-[#B026FF] text-white">
                  <CheckCircle className="w-4 h-4 mr-2" />{viewProfileRequest.status === "approved" ? "Already Approved ✓" : "Approve"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Profiles by Name Dialog */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-[#0B0A11] border border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-manrope text-rose-400 flex items-center gap-2"><Trash2 className="w-5 h-5" />Delete Profiles by Name</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs text-white/60">Profile names (comma separated)</Label>
              <Textarea placeholder="Priya Sharma, Rahul Verma, ..." value={deleteNames} onChange={(e) => setDeleteNames(e.target.value)} className="min-h-[100px] bg-black/30 border-white/10 text-white mt-1" />
            </div>
            <div className="bg-rose-500/10 border border-rose-400/30 rounded-xl p-3"><p className="text-sm text-rose-300">⚠ This permanently deletes profiles from all collections. Cannot be undone.</p></div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowDeleteModal(false)} className="flex-1 bg-white/5 border-white/10 text-white hover:bg-white/10">Cancel</Button>
              <Button onClick={handleDeleteProfilesByName} disabled={deletingProfiles || !deleteNames.trim()} className="flex-1 bg-rose-500 hover:bg-rose-600 text-white">{deletingProfiles ? "Deleting..." : "Delete"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Migration Dialog */}
      <Dialog open={showMigrationModal} onOpenChange={setShowMigrationModal}>
        <DialogContent className="sm:max-w-lg bg-[#0B0A11] border border-white/10 text-white">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-manrope text-orange-400"><TrendingUp className="w-5 h-5" />Data Migration</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-orange-500/10 border border-orange-400/30 rounded-xl p-4"><p className="text-sm text-orange-200">This imports all existing influencer data to the new talents system. Safe — no data deleted.</p></div>
            <div className="space-y-2">
              <p className="font-manrope font-semibold text-white text-sm">Migrations:</p>
              <ul className="text-sm text-white/60 space-y-1.5">
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-[#FF0080] rounded-full" />Migrate influencers → talents</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-[#FF0080] rounded-full" />Add verified profiles from live site</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-[#FF0080] rounded-full" />Migrate paid verified users</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-[#FF0080] rounded-full" />Sync talents to users (fix login)</li>
              </ul>
            </div>
            {migrationResults.length > 0 && (
              <div className="border border-white/10 rounded-xl p-4 space-y-2 bg-black/20">
                <p className="font-manrope font-semibold text-white text-sm">Results:</p>
                {migrationResults.map((r, i) => (
                  <div key={i} className={`flex items-start gap-2 text-sm ${r.success ? "text-emerald-400" : "text-rose-400"}`}>
                    {r.success ? <CheckCircle className="w-4 h-4 mt-0.5" /> : <XCircle className="w-4 h-4 mt-0.5" />}
                    <div><p className="font-manrope">{r.name}</p><p className="text-xs opacity-80">{r.message}</p></div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowMigrationModal(false)} className="flex-1 bg-white/5 border-white/10 text-white hover:bg-white/10">Cancel</Button>
              <Button onClick={runMigration} disabled={migrationRunning} className="flex-1 bg-gradient-to-r from-[#FF0080] to-[#B026FF] text-white">{migrationRunning ? "Running..." : "Run Migration"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Opportunity Dialog */}
      <Dialog open={showCreateOpportunity} onOpenChange={setShowCreateOpportunity}>
        <DialogContent className="max-w-lg bg-[#0B0A11] border border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-manrope">Create New Opportunity</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs text-white/60">Title</Label><Input placeholder="Instagram Post for FoundStar" value={newOpportunity.title} onChange={(e) => setNewOpportunity({ ...newOpportunity, title: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
            <div><Label className="text-xs text-white/60">Description</Label><Input placeholder="What the creator needs to do" value={newOpportunity.description} onChange={(e) => setNewOpportunity({ ...newOpportunity, description: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-white/60">Payment (₹)</Label><Input type="number" value={newOpportunity.payment_amount} onChange={(e) => setNewOpportunity({ ...newOpportunity, payment_amount: parseInt(e.target.value) })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
              <div><Label className="text-xs text-white/60">Type</Label>
                <select className="w-full p-2 mt-1 bg-black/30 border border-white/10 rounded-md text-white" value={newOpportunity.payment_type} onChange={(e) => setNewOpportunity({ ...newOpportunity, payment_type: e.target.value })}>
                  <option value="per_post">Per Post</option><option value="per_story">Per Story</option><option value="per_reel">Per Reel</option><option value="per_video">Per Video</option><option value="fixed">Fixed</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-white/60">Platform</Label>
                <select className="w-full p-2 mt-1 bg-black/30 border border-white/10 rounded-md text-white" value={newOpportunity.platform} onChange={(e) => setNewOpportunity({ ...newOpportunity, platform: e.target.value })}>
                  <option>Instagram</option><option>YouTube</option><option>Facebook</option><option>Twitter</option>
                </select>
              </div>
              <div><Label className="text-xs text-white/60">Deadline (days)</Label><Input type="number" value={newOpportunity.deadline_days} onChange={(e) => setNewOpportunity({ ...newOpportunity, deadline_days: parseInt(e.target.value) })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-white/60">Min Followers</Label><Input type="number" value={newOpportunity.min_followers} onChange={(e) => setNewOpportunity({ ...newOpportunity, min_followers: parseInt(e.target.value) })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
              <div><Label className="text-xs text-white/60">Max Applications</Label><Input type="number" value={newOpportunity.max_applications} onChange={(e) => setNewOpportunity({ ...newOpportunity, max_applications: parseInt(e.target.value) })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="verified_only" checked={newOpportunity.verified_only} onChange={(e) => setNewOpportunity({ ...newOpportunity, verified_only: e.target.checked })} />
              <Label htmlFor="verified_only" className="text-sm text-white/70">Verified creators only</Label>
            </div>
            <Button onClick={createOpportunity} className="w-full bg-gradient-to-r from-[#FF0080] to-[#B026FF] text-white">Create Opportunity</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
